import { AfterViewInit, ChangeDetectorRef, Component, HostListener, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ConfirmDialog } from '../modals/confirm-dialog.component';
import { SendEmailDialog } from '../send-email-popup/send-email-popup.component';
import { EmployeeMultiselectDialog } from '../employee-popup/employee-popup.component';
import { ApplicationControlService } from '../../services/applicationcontrol.service';
import { AzureMapsService } from '../../services/azure-maps.service';
import { CityService } from '../../services/city.service';
import { EmployeeService } from '../../services/employees.service';
import { EmailService } from '../../services/email.service';
import { EncryptionService } from '../../services/encryption.service';
import { ContactService } from '../../services/contact.service';
import { CRMService } from '../../services/crm.service';
import { CSEService } from '../../services/cse.service';
import { NotificationService } from '../../services/notification.service';
import { OrderService } from '../../services/order.service';
import { RecordLockService } from '../../services/recordlock.service';
import { TMWService } from '../../services/tmw.service';
import { UtilService } from '../../services/util.service';
import {
    CarrierLane
    , CarrierList
    , CarrierLookup
    , CommodityType
    , Contact
    , CustomerCarrierExclusionMap
    , CustomerLookup
    , Employee
    , EquipmentClass
    , InvoiceStatus
    , LoadStatus
    , Mode
    , Opportunity
    , OrderStop
    , Product
    , QuoteOrder
    , QuoteOrderCopy
    , QuoteOrderLine
    , QuoteOrderLinePriority
    , QuoteOrderLineSplit
    , QuoteOrderType
    , RateIndex
    , SearchCarrier
    , SearchCityState
    , SearchCustomer
    , SearchInvoiceLine
    , SearchModeEquipmentClassTrailerType
    , SearchOrderEventSchedule
    , SearchOrderEventType
    , SearchOrderStop
    , SearchQuoteOrder
    , SearchQuoteOrderLine
    , TrailerType
    , TravelEstimate
} from '../../models/models';

@Component({
    selector: 'order-form',
    templateUrl: './order-form.component.html',
    standalone: false
})
export class OrderForm implements AfterViewInit, OnChanges, OnDestroy, OnInit {
    @Input() quoteOrderID: number = 0;
    @HostListener('window:beforeunload', ['$event'])
    beforeunloadHandler(event) {
        event.preventDefault();
        if (this.quoteOrder.quoteOrderID > 0) {
            event.returnValue = 'Any unsaved data will be lost, and the Order will be unlocked.';
            //console.log("beforeunloadHandler", this.quoteOrder.quoteOrderID, this.recordLockSource);
            this.recordLockService.clearRecordLock("QuoteOrder", this.quoteOrder.quoteOrderID, this.recordLockSource);
        }
        return false;
    }

    public orderFormType: string = "Order";
    public encryptedOrderNumber: string = "";
    public externalOrderNumber: string = "";

    private minStopDate?: Date;
    private maxStopDate?: Date;

    public isEditQuoteOrderLine: boolean = false;
    public isEditCarrierReps: boolean = false;
    public isEditCarrierServices: boolean = false;
    public isSetLoadTags: boolean = false;
    public isExpedite: boolean = false;
    public isAccountManager: boolean = false;
    public isValidExternalTrackingLink: boolean = true;

    public csrQuoteOrderLineID: number = 0;
    public carrierServiceQuoteOrderLineID: number = 0;
    public tagsQuoteOrderLineID: number = 0;
    public isCopyOrderAsMaster: boolean = false;
    public isCopyOrder: boolean = false;
    public isCopySingleOrder: boolean = false;
    public travelEstimate: TravelEstimate = {} as TravelEstimate;
    public totalMiles: number = 0;
    public totalPrice: number = 0;
    public totalCost: number = 0;
    public totalLinehaulCost: number = 0;
    public totalFuelCost: number = 0;
    public totalAccessorialCost: number = 0;
    public payDetailTotal: number = 0;
    public thirdPartyPayTotal: number = 0;
    public isSplit: boolean = false;

    // Order Bill To
    public billToID: number = 0;
    public isAddBillTo: boolean = false;
    public isEditBillTo: boolean = false;
    public billTo: SearchCustomer = {} as SearchCustomer;
    //public billToNotesList: SearchCustomerNotes[] = [];

    // Order Shipper
    public shipperID: number = 0;
    public isAddShipper: boolean = false;
    public isEditShipper: boolean = false;
    public shipper: SearchCustomer = {} as SearchCustomer;
    public shipperEarliestDate: Date = new Date();
    public shipperEarliestDateUTC: Date = new Date();
    public shipperLatestDate: Date = new Date();
    public shipperLatestDateUTC: Date = new Date();
    public isShipperEarliestValid: boolean = true;
    public isShipperLatestValid: boolean = true;
    public shipperHasAppointment: boolean = true;
    //public shipperNotesList: SearchCustomerNotes[] = [];

    // Quote/RFQ Pick City
    public pickCityID: number = 0;
    public pickCity: SearchCityState = {} as SearchCityState;

    // Order Consignee
    public consigneeID: number = 0;
    public isAddConsignee: boolean = false;
    public isEditConsignee: boolean = false;
    public consignee: SearchCustomer = {} as SearchCustomer;
    public consigneeEarliestDate: Date = new Date();
    public consigneeEarliestDateUTC: Date = new Date();
    public consigneeLatestDate: Date = new Date();
    public consigneeLatestDateUTC: Date = new Date();
    public isConsigneeEarliestValid: boolean = true;
    public isConsigneeLatestValid: boolean = true;
    public consigneeHasAppointment: boolean = true;
    //public consigneeNotesList: SearchCustomerNotes[] = [];

    // Quote/RFQ Drop City
    public dropCityID: number = 0;
    public dropCity: SearchCityState = {} as SearchCityState;

    // Split Shipper
    public isAddQuoteOrderLineShipper: boolean = false;
    public isEditQuoteOrderLineShipper: boolean = false;
    public quoteOrderLineShipper: SearchCustomer = {} as SearchCustomer;
    public quoteOrderLineShipperEarliestDate: Date = new Date();
    public quoteOrderLineShipperLatestDate: Date = new Date();
    public isQuoteOrderLineShipperEarliestValid: boolean = true;
    public isQuoteOrderLineShipperLatestValid: boolean = true;

    // Split Consignee
    public isAddQuoteOrderLineConsignee: boolean = false;
    public isEditQuoteOrderLineConsignee: boolean = false;
    public quoteOrderLineConsignee: SearchCustomer = {} as SearchCustomer;
    public quoteOrderLineConsigneeEarliestDate: Date = new Date();
    public quoteOrderLineConsigneeLatestDate: Date = new Date();
    public isQuoteOrderLineConsigneeEarliestValid: boolean = true;
    public isQuoteOrderLineConsigneeLatestValid: boolean = true;

    // Stop Customer
    public isAddOrderStopCustomer: boolean = false;
    public isEditOrderStopCustomer: boolean = false;
    public orderStopCustomer: SearchCustomer = {} as SearchCustomer;
    public orderStopEarliestDate: Date = new Date();
    public orderStopLatestDate: Date = new Date();
    public isOrderStopEarliestValid: boolean = true;
    public isOrderStopLatestValid: boolean = true;

    // Carrier Offer
    public isAddOffer: boolean = false;
    public isExcludeNonRMISCertified: boolean = true;
    public offerID: number = 0;
    private offerQuoteOrderLineID: number = 0;
    private offerQuoteOrderLine: QuoteOrderLine = {} as QuoteOrderLine;
    public carrierFormGroup: UntypedFormGroup = new UntypedFormGroup({
        carrierLookupControl: new UntypedFormControl('')
    });
    private carrier: SearchCarrier = {} as SearchCarrier;
    private editedCarrierList: CarrierList | undefined;
    private carrierID: number = 0;
    public isCustomerExcluded: boolean = false;
    public customerCarrierExclusionMapList: CustomerCarrierExclusionMap[] = [];

    public createdByEmployeeName: string = "";
    public lastModifiedByEmployeeName: string = "";

    public quoteOrder: QuoteOrder = {} as QuoteOrder;
    public searchQuoteOrder: SearchQuoteOrder = {} as SearchQuoteOrder;
    public quoteOrderLineList: QuoteOrderLine[] = [];
    public quoteOrderLine: QuoteOrderLine = {} as QuoteOrderLine;
    public searchQuoteOrderLine: SearchQuoteOrderLine = {} as SearchQuoteOrderLine;
    public quoteOrderLineSplit: QuoteOrderLineSplit = {} as QuoteOrderLineSplit;
    public searchScheduleList: SearchOrderEventSchedule[] = [];
    public newOrderStop: OrderStop = {} as OrderStop;
    public searchOrderStopList: SearchOrderStop[] = [];
    public opportunity: Opportunity = {} as Opportunity;
    public commodityTypes: CommodityType[] = [];
    public invoiceStatusList: InvoiceStatus[] = [];
    public filteredInvoiceStatusList: InvoiceStatus[] = [];
    public isPrepared: boolean = false;
    public isMasterOrderLocked: boolean = false;
    public loadStatusList: LoadStatus[] = [];
    public quoteOrderTypeList: QuoteOrderType[] = [];
    public modeList: Mode[] = [];
    public equipmentClassList: EquipmentClass[] = [];
    public trailerTypeList: TrailerType[] = [];
    public modeEquipmentClassTrailerTypeList: SearchModeEquipmentClassTrailerType[] = [];
    public quoteOrderLinePriorityList: QuoteOrderLinePriority[] = [];
    public rateIndexList: RateIndex[] = [];
    public searchOrderEventTypes: SearchOrderEventType[] = [];
    public dropStopEventTypes: SearchOrderEventType[] = [];
    public pickStopEventTypes: SearchOrderEventType[] = [];
    public splitDropStopEventTypes: SearchOrderEventType[] = [];
    public splitPickStopEventTypes: SearchOrderEventType[] = [];
    public orderStopEventTypes: SearchOrderEventType[] = [];
    public filteredStopEventTypes: SearchOrderEventType[] = [];

    public loadsRefreshDate: Date = new Date();
    public stopsRefreshDate: Date = new Date();
    public tagsRefreshDate: Date = new Date();

    // Copy Order
    public quoteOrderCopy: QuoteOrderCopy = {} as QuoteOrderCopy;
    public isCopyOrderBeginDateValid: boolean = true;

    public productList: Product[] = [];
    public opportunityList: Opportunity[] = [];
    public quoteOrders: QuoteOrder[] = [];
    public allEmployeeList: Employee[] = [];
    public ownerEmployeeList: Employee[] = [];

    private setQuoteOrderTypeValue: string = "";

    private recordLockAction: string = "";
    private recordLockSource: string = "Order Entry";
    private recordLockID: number = 0;
    public hasRecordLock: boolean = false;

    public customerFormGroup: UntypedFormGroup = new UntypedFormGroup({
        billToCtrl: new UntypedFormControl(''),
        pickCityCtrl: new UntypedFormControl(''),
        dropCityCtrl: new UntypedFormControl(''),
        shipperCtrl: new UntypedFormControl(''),
        consigneeCtrl: new UntypedFormControl(''),
        quoteOrderLineShipperCtrl: new UntypedFormControl(''),
        quoteOrderLineConsigneeCtrl: new UntypedFormControl(''),
        orderStopCustomerCtrl: new UntypedFormControl('')
    });

    constructor(private applicationControlService: ApplicationControlService,
        private azureService: AzureMapsService,
        private cityService: CityService,
        private contactService: ContactService,
        private crmService: CRMService,
        private cseService: CSEService,
        private emailService: EmailService,
        private _employeeService: EmployeeService,
        private encryptionService: EncryptionService,
        private orderService: OrderService,
        private notificationService: NotificationService,
        private route: ActivatedRoute,
        private router: Router,
        private recordLockService: RecordLockService,
        private titleService: Title,
        private tmwService: TMWService,
        private utilService: UtilService,
        public dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        @Inject('BASE_URL') private baseUrl: string) {
        route.params.subscribe(params => {
            //console.log("constructor", this.quoteOrderID, params.quoteOrderID);
            if (this.quoteOrderID > 0 && this.quoteOrderID != params.quoteOrderID) {
                // Changing from one order to another or to new order, so clear previous record lock
                //this.recordLockAction = "";
                //this.recordLockService.clearRecordLock("QuoteOrder", this.quoteOrder.quoteOrderID, this.recordLockSource);
                //this.recordLockService.clearRecordLock(this.recordLockService.recordLock.recordLockID);
            }
            if (this.quoteOrderID != params.quoteOrderID || params.quoteOrderID == 0) {
                this.quoteOrderID = params.quoteOrderID;
                this.init();
            }
            else {
                this.quoteOrderID = params.quoteOrderID;
            }
            this.titleService.setTitle("Order Entry - " + this.quoteOrderID);
        });
    }

    ngOnInit() {
        //console.log("ngOnInit", this.quoteOrderID);
        //this.init();
        this.isAccountManager = this.getIsAccountManager();
        this.initRecordLock();
        //window.addEventListener("beforeunload", function (event) {
        //    var client = new XMLHttpRequest();
        //    client.open("POST", "http://url/to/endsession", false); // third parameter indicates sync xhr
        //    client.setRequestHeader("Content-Type", "application/json");
        //    client.send('{userId: "42"}');
        //    console.log('done!!!'); // outputs when a response is received 
        //});

    }

    ngOnChanges(changes: SimpleChanges) {
        //console.log("ngOnChanges", this.quoteOrderID);
    }

    ngOnDestroy() {
        //console.log("ngOnDestroy", this.quoteOrder.quoteOrderID, this.recordLockSource);
        this.recordLockService.clearRecordLock("QuoteOrder", this.quoteOrder.quoteOrderID, this.recordLockSource);
    }

    ngAfterViewInit() {
        //console.log("ngAfterViewInit", this.quoteOrderID);
        //this.cdr.detectChanges();
    }

    private getIsAccountManager() {
        var employeeRoles = localStorage.getItem('EmployeeRoles');

        //console.log(employeeRoles);
        if (employeeRoles != null && employeeRoles.length > 0) {
            if (employeeRoles.indexOf('Account Manager') > -1) return true;
        }

        return false;
    }

    private init() {
        this.setApplicationControls();
        this.getInvoiceStatusList();
        this.getLoadStatusList();
        this.getQuoteOrderTypes();
        this.getCommodityTypes();
        this.getProducts();
        this.getSearchModeEquipmentClassTrailerTypes();
        this.getQuoteOrderLinePriorities();
        this.getEmployees();
        this.getRateIndexes();
        this.getSearchOrderEventTypes();

        //console.log("init", this.quoteOrderID);

        if (this.quoteOrderID > 0) {
            this.getQuoteOrder(this.quoteOrderID);
        }
        else {
            this.hasRecordLock = true;
            this.getNewQuoteOrder();
        }

        //this.getSearchOrderStop();
    }

    private setApplicationControls() {
        //this.applicationControlService.getApplicationControlsByAppName("RequiredTravelBreakHours").subscribe(result => {
        //    if (result.length > 0) {
        //        this.requiredTravelBreakHours = parseInt(result[0].appSettingValue);
        //    }
        //});
        //this.applicationControlService.getApplicationControlsByAppName("MaxTravelMilesBeforeBreak").subscribe(result => {
        //    if (result.length > 0) {
        //        this.maxTravelMilesBeforeBreak = parseInt(result[0].appSettingValue);
        //    }
        //});
        this.applicationControlService.getApplicationControlsByAppName("IsExcludeNonRMISCertified", false).subscribe(result => {
            if (result.length > 0) {
                var appValue = result[0].appSettingValue;
                if (appValue == "1") {
                    this.isExcludeNonRMISCertified = true;
                }
                else {
                    this.isExcludeNonRMISCertified = false;
                }
            }
        });
    }

    public navigateQuoteOrder(quoteOrderID: number, isLoadOrder: boolean) {
        //console.log("navigatQuoteOrder", quoteOrderID);
        this.quoteOrderID = quoteOrderID
        this.router.navigate(['order-entry', this.quoteOrderID]);
        if (isLoadOrder) {
            this.hasRecordLock = false;
            this.getQuoteOrder(this.quoteOrderID);
        }
    }

    private getInvoiceStatusList() {
        this.orderService.getInvoiceStatuses().subscribe(result => {
            //var list = result.filter(t => t.name != "Available" && t.name != "Prepared");
            //this.invoiceStatusList = list;
            this.invoiceStatusList = result;
            var invoiceStatusID = this.invoiceStatusList.find(t => t.name == "Pending")!.invoiceStatusId;
            if (this.quoteOrder.invoiceStatusID == 0) {
                this.quoteOrder.invoiceStatusID = invoiceStatusID;
                this.filterInvoiceStatusList();
            }
        });
    }

    private filterInvoiceStatusList() {
        if (this.searchQuoteOrderLine.orderLoadStatus == "Completed") {
            this.filteredInvoiceStatusList = this.invoiceStatusList.filter(t => t.name != "Pending" && t.name != "Prepared");
        }
        else {
            this.filteredInvoiceStatusList = this.invoiceStatusList.filter(t => t.name != "Available" && t.name != "Prepared");
        }
    }

    private getLoadStatusList() {
        this.orderService.getLoadStatuses().subscribe(result => {
            this.loadStatusList = result.resultCollection;
        });
    }

    public canExportOrder() {
        var ret: boolean = true;
        if (this.orderFormType != "Order") {
            ret = false;
        }
        if (this.searchQuoteOrderLine.orderLoadStatus == "Canceled") {
            ret = false;
        }
        if (this.quoteOrder.isMasterOrder == true) {
            ret = false;
        }
        if (this.quoteOrder.isExternalOrder == true) {
            ret = false;
        }
        if (this.quoteOrder.externalOrderNumber != null && this.quoteOrder.externalOrderNumber != "") {
            ret = false;
        }
        return ret;
    }

    public canSyncOrder() {
        var ret: boolean = true;
        if (this.quoteOrder.isExternalOrder == false) {
            ret = false;
        }
        if (this.quoteOrder.externalOrderNumber == null || this.quoteOrder.externalOrderNumber == "") {
            ret = false;
        }
        return ret;
    }

    public canChangeMaster() {
        var ret: boolean = true;
        var carrierLoads = this.quoteOrderLineList.filter(t => t.carrierID > 0);
        if (carrierLoads.length > 0) {
            ret = false;
        }
        if (this.isMasterOrderLocked) {
            ret = false;
        }
        if (this.searchScheduleList.length > 0) {
            ret = false;
        }
        return ret;
    }

    public canConvertToOrder(orderType: string) {
        var ret: boolean = false;
        if (this.quoteOrderTypeList.length > 0) {
            var quoteOrderType: QuoteOrderType = this.quoteOrderTypeList.find(t => t.abbrev == orderType || t.description == orderType)!;
            var custStops = this.searchOrderStopList.filter(t => t.customerID != undefined && t.customerID > 0);
            //var cgLines = this.quoteOrderLineList.filter(t => t.quoteOrderLineTypeID == orderType.quoteOrderTypeID);
            if (this.isMasterOrderLocked != true
                && this.quoteOrderLineList.length > 0
                && this.searchOrderStopList.length > 0
                //&& orderType != null
                && this.quoteOrder.quoteOrderTypeID == quoteOrderType.quoteOrderTypeID
                //&& cgLines.length == this.quoteOrderLineList.length
                && custStops.length == this.searchOrderStopList.length
                && this.quoteOrder.quoteOrderID > 0) {
                if (orderType == "CG") {
                    ret = true;
                }
                else if (orderType == "Quote" && this.quoteOrder.shipperId > 0 && this.quoteOrder.consigneeId > 0) {
                    ret = true;
                }
            }
        }
        return ret;
    }

    public canCancel() {
        var ret: boolean = true;
        if (this.orderFormType != "Order" && this.orderFormType != "Can Get" && this.orderFormType != "Quote") {
            ret = false;
        }
        if (this.quoteOrder.quoteOrderID == 0) {
            ret = false;
        }
        if (this.quoteOrderLineList.length == 0) {
            ret = false;
        }
        if (this.isMasterOrderLocked) {
            ret = false;
        }
        if (this.quoteOrder.isMasterOrder == true) {
            ret = false;
        }
        for (var i = 0; i < this.quoteOrderLineList.length; i++) {
            if (this.quoteOrderLineList[i].carrierID > 0) {
                ret = false;
            }
            var statuses: LoadStatus[] = this.loadStatusList.filter(t =>
                t.description == "Planned"
                || t.description == "Available");
            var status = statuses.find(t => t.loadStatusID == this.quoteOrderLineList[i].loadStatusID);
            if (status == null) {
                ret = false;
            }
            //console.log("canCancel", status, statuses, this.quoteOrderLineList);
        }
        return ret;
    }

    public canUnCancel() {
        var ret: boolean = true;
        if (this.quoteOrder.quoteOrderID == 0) {
            ret = false;
        }
        if (this.quoteOrderLineList.length == 0) {
            ret = false;
        }
        if (this.isMasterOrderLocked) {
            ret = false;
        }
        var cancelStatus = this.loadStatusList.find(t => t.description == "Canceled");
        if (cancelStatus != null) {
            var loadList = this.quoteOrderLineList.filter(t => t.loadStatusID == cancelStatus!.loadStatusID);
            if (loadList.length == 0) {
                ret = false;
            }
        }
        //for (var i = 0; i < this.quoteOrderLineList.length; i++) {
        //    var statuses: LoadStatus[] = this.loadStatusList.filter(t => t.description == "Canceled");
        //    var status = statuses.find(t => t.loadStatusID == this.quoteOrderLineList[i].loadStatusID);
        //    if (status == null) {
        //        ret = false;
        //    }
        //}
        return ret;
    }

    private getQuoteOrderTypes() {
        this.orderService.getQuoteOrderTypes().subscribe(result => {
            this.quoteOrderTypeList = result;
            if (this.quoteOrderID == 0) {
                this.setQuoteOrderTypeFromFormType();
                //this.navigateQuoteOrder(0, true);
            }
        });
    }

    private getCommodityTypes() {
        this.utilService.isLoading = true;
        this.orderService.getAllCommodityTypes().subscribe(result => {
            this.utilService.isLoading = false;
            this.commodityTypes = result.resultCollection;
            this.commodityTypes = this.commodityTypes.sort((left, right): number => {
                if (left.name < right.name) return -1;
                if (left.name > right.name) return 1;
                return 0;
            });
        });
    }

    private getProducts() {
        this.orderService.getProducts().subscribe(result => {
            this.productList = result.filter(t => t.isOrderEntryCompatible == true);

            if (this.quoteOrderID == 0) {
                var productID = 0;
                if (this.productList.length > 0) {
                    productID = this.productList.find(t => t.productName == "Brokerage")!.productId;
                    this.opportunity.productId = productID;
                }
            }
        });
    }

    private getOpportunityByID(opportunityID: number) {
        this.crmService.getOpportunityById(opportunityID).subscribe(result => {
            //console.log("getOpportunityByID", opportunityID, this.quoteOrder.opportunityID);
            if (result != null) {
                this.opportunity = result;
                this.productChanged(result.productId);
            }
            else {
                this.opportunity = {} as Opportunity;
            }
        });
    }

    private getOpportunities(customerId: number, productId: number, productName: string) {
        this.crmService.getOpportunitiesByCustomerIdProductId(customerId, productId).subscribe(result => {
            // need to add new opportunity if it doesn't exist
            // Spot-Unknown : for Spot/Expedite RateIndex
            // Contract-Unknown : for Contract RateIndex
            var rateIndex = this.rateIndexList.find(t => t.rateIndexID == this.quoteOrder.rateIndexId);
            var notes = "UNKNOWN";
            if (rateIndex != null) {
                if (rateIndex.description == "Contract") {
                    notes = "Contract - Unknown";
                }
                else {
                    notes = "Spot - Unknown";
                }
            }
            if (productName == "Consol/Deconsol") {
                notes = "Consol - Unknown";
            }
            this.opportunityList = Object.assign([], result);
            if (this.quoteOrder.customerID != customerId || this.quoteOrder.quoteOrderID == null || this.quoteOrder.quoteOrderID == 0) {
                result = result.filter(t => t.notes != "UNKNOWN" && t.notes == notes);
                if (result.length == 0) {
                    var opp: Opportunity = {
                        opportunityID: 0,
                        customerId: customerId,
                        productId: productId,
                        //contactId: 0,
                        //marketingCampaignId: 0,
                        estimatedRevenue: 0,
                        notes: notes,
                        dueDate: new Date(),
                        expectedCloseDate: new Date(),
                        createDate: new Date(),
                        ownerEmployeeId: this.quoteOrder.orderOwnerEmployeeID
                    }
                    this.crmService.saveOpportunity(opp).subscribe(result2 => {
                        //this.opportunityList.length = 0;
                        this.opportunityList.push(result2);
                        if (result2 != null && result2.opportunityID > 0) {
                            this.quoteOrder.opportunityID = result2.opportunityID;
                        }
                        console.log("getOpportunities:saveOpportunity", this.quoteOrder.opportunityID, customerId, productId, result2);
                    });
                }
                else {
                    var idx = this.opportunityList.findIndex(t => t.opportunityID == this.quoteOrder.opportunityID);
                    if (idx < 0) {
                        idx = this.opportunityList.findIndex(t => t.notes.indexOf(" - Unknown") >= 0);
                        if (idx >= 0) {
                            this.quoteOrder.opportunityID = this.opportunityList[idx].opportunityID;
                        }
                    }
                }
            }
            //console.log("getOpportunities", this.quoteOrder.opportunityID, customerId, productId, this.opportunityList);
        });
    }

    private getRateIndexes() {
        this.crmService.getRateIndices().subscribe(result => {
            this.rateIndexList = result;
            //    var idx = this.rateIndexList.findIndex(t => t.description == "Spot")!;
            //    if (idx >= 0) {
            //        this.rateIndexID = this.rateIndexList[idx].rateIndexID;
            //    }
        });
    }

    private getSearchQuoteOrderLine(quoteOrderLineID: number) {
        this.orderService.getSearchQuoteOrderLineByID(quoteOrderLineID).subscribe(result => {
            if (result.resultCollection.length > 0) {
                this.searchQuoteOrderLine = result.resultCollection[0];
                this.quoteOrderLine.rateIndexID = this.searchQuoteOrderLine.rateIndexID;
                this.filterInvoiceStatusList();
            }
        });
    }

    private getModes() {
        for (var i = 0; i < this.modeEquipmentClassTrailerTypeList.length; i++) {
            var idx = this.modeList.findIndex(t => t.modeID == this.modeEquipmentClassTrailerTypeList[i].modeID);
            if (idx < 0) {
                var mode: Mode = {
                    modeID: this.modeEquipmentClassTrailerTypeList[i].modeID,
                    abbrev: this.modeEquipmentClassTrailerTypeList[i].modeAbbrev,
                    description: this.modeEquipmentClassTrailerTypeList[i].modeDescription,
                    groupEmail: "",
                    defaultTrailerTypeId: 0,
                    includeInLogisticLaneInd: false,
                    sortOrder: this.modeEquipmentClassTrailerTypeList[i].modeSortOrder
                }
                this.modeList.push(mode);
            }
        }
        //console.log("getModes", this.quoteOrder.quoteOrderID, this.modeList);
    }

    private getEquipmentClasses() {
        var subList = this.modeEquipmentClassTrailerTypeList.filter(t => t.modeID == this.quoteOrderLine.modeID);
        for (var i = 0; i < subList.length; i++) {
            var idx = this.equipmentClassList.findIndex(t => t.equipmentClassID == subList[i].equipmentClassID);
            if (idx < 0) {
                var equipmentClass: EquipmentClass = {
                    equipmentClassID: subList[i].equipmentClassID,
                    abbreviation: subList[i].equipmentClassAbbrev,
                    className: subList[i].equipmentClassName,
                    sortOrder: subList[i].equipmentClassSortOrder
                }
                this.equipmentClassList.push(equipmentClass);
            }
        }
        //console.log("getEquipmentClasses", this.equipmentClassList);
    }

    private getTrailerTypes() {
        var subList = this.modeEquipmentClassTrailerTypeList.filter(t => t.modeID == this.quoteOrderLine.modeID && t.equipmentClassID == this.quoteOrderLine.equipmentClassID);
        for (var i = 0; i < subList.length; i++) {
            var idx = this.trailerTypeList.findIndex(t => t.trailerTypeID == subList[i].trailerTypeID);
            if (idx < 0) {
                var trailerType: TrailerType = {
                    trailerTypeID: subList[i].trailerTypeID,
                    abbrev: subList[i].trailerTypeAbbrev,
                    description: subList[i].trailerTypeDescription,
                    defaultModeID: subList[i].defaultModeID!,
                    allowedOnLanes: subList[i].allowedOnLanes!,
                    defaultLength: subList[i].defaultLength,
                    defaultWeight: subList[i].defaultWeight,
                    datEquipmentCode: "",
                    datEquipmentTypeId: 0,
                    erpCode: "",
                    includeInLogisticLaneInd: false,
                    sortOrder: subList[i].trailerTypeSortOrder
                }
                this.trailerTypeList.push(trailerType);
            }
        }
        //console.log("getTrailerTypes", this.trailerTypeList);
    }

    private getSearchModeEquipmentClassTrailerTypes() {
        this.orderService.getAllSearchModeEquipmentClassTrailerType().subscribe(result => {
            this.modeEquipmentClassTrailerTypeList = result.resultCollection;
            this.getModes();
            this.setDefaults();
        });
    }

    public checkPieces() {
        if (this.quoteOrderLine.pieces > 1000000) {
            this.quoteOrderLine.pieces = 1000000;
        }
        if (this.quoteOrderLine.pieces < 0) {
            this.quoteOrderLine.pieces = 0;
        }
    }

    public checkWeight() {
        if (this.quoteOrderLine.weight > 1000000) {
            this.quoteOrderLine.weight = 1000000;
        }
        if (this.quoteOrderLine.weight < 0) {
            this.quoteOrderLine.weight = 0;
        }
    }

    private setDefaults() {
        if (this.quoteOrderID == 0 && this.quoteOrderLine.quoteOrderLineID == 0) {
            var modeEquipmentClassTrailerType = this.modeEquipmentClassTrailerTypeList.find(t =>
                t.modeAbbrev == "TL"
                && t.equipmentClassAbbrev == "V"
                && t.trailerTypeAbbrev == "V"
            );
            //console.log("modeEquipmentClassTrailerType", modeEquipmentClassTrailerType, this.modeEquipmentClassTrailerTypeList);
            if (modeEquipmentClassTrailerType != null) {
                this.quoteOrderLine.modeID = modeEquipmentClassTrailerType.modeID;
                this.quoteOrderLine.equipmentClassID = modeEquipmentClassTrailerType.equipmentClassID;
                this.quoteOrderLine.trailerTypeID = modeEquipmentClassTrailerType.trailerTypeID;
                this.quoteOrderLine.trailerLength = modeEquipmentClassTrailerType.defaultLength;
                this.getEquipmentClasses();
                this.getTrailerTypes();
            }
        }
        //console.log("setDefaults", this.quoteOrderID, this.quoteOrder.quoteOrderID, this.quoteOrderLine);
    }

    private getQuoteOrderLinePriorities() {
        this.orderService.getQuoteOrderLinePriorities().subscribe(result => {
            this.quoteOrderLinePriorityList = result.resultCollection;

            if (this.quoteOrderID == 0 && this.quoteOrderLine) {
                var priorityID = 0;
                if (this.quoteOrderLinePriorityList.length > 0) {
                    priorityID = this.quoteOrderLinePriorityList.find(t => t.abbrev == "Regular")!.quoteOrderLinePriorityID;
                    this.quoteOrderLine.quoteOrderLinePriorityID = priorityID;
                }
            }
        });
    }

    private getSearchOrderEventTypes() {
        this.utilService.isLoading = true;
        this.orderService.getGetAllSearchOrderEventType().subscribe(result => {
            this.utilService.isLoading = false;
            this.searchOrderEventTypes = result;

            this.orderStopEventTypes = this.searchOrderEventTypes.filter(c =>
                c.eventTypeName == "Order Stop"
                && (
                    c.code == "LUL"
                    || c.code == "LLD"
                    || c.code == "NBS"
                    || c.code == "NBCST"
                )
            );

            this.filteredStopEventTypes = Object.assign([], this.orderStopEventTypes);

            var dropStopTypes = this.searchOrderEventTypes.filter(c =>
                c.eventTypeName == "Order Stop" && c.reportDescription == "Delivery");
            dropStopTypes = dropStopTypes.sort((left, right): number => {
                if (left.code < right.code) return -1;
                if (left.code > right.code) return 1;
                return 0;
            });

            this.dropStopEventTypes = dropStopTypes;
            this.splitDropStopEventTypes = dropStopTypes.filter(t => t.code == "DLT");
            var pickStopTypes = this.searchOrderEventTypes.filter(c =>
                c.eventTypeName == "Order Stop" && c.reportDescription == "Pickup");
            pickStopTypes = pickStopTypes.sort((left, right): number => {
                if (left.code < right.code) return -1;
                if (left.code > right.code) return 1;
                return 0;
            });
            this.pickStopEventTypes = pickStopTypes;
            this.splitPickStopEventTypes = pickStopTypes.filter(t => t.code == "HLT");
        });
    }

    private getEmployees() {
        this._employeeService.getActiveEmployees().subscribe(result => {
            this.allEmployeeList = result;
            this.ownerEmployeeList = result.filter(t => t.activeInd == true && t.firstName != "New");
            if (this.quoteOrderID == 0) {
                var employeeID = parseInt(localStorage.getItem('EmployeeId')!);
                var employees = this.ownerEmployeeList.filter(t => t.employeeId == employeeID);
                if (employees.length > 0) {
                    this.quoteOrder.orderOwnerEmployeeID = employeeID;
                }
            }
        });
    }

    private setEmployeeNames() {
        var createdBy = this.allEmployeeList.find(t => t.employeeId == this.quoteOrder.createdByEmployeeID);
        if (createdBy != null) {
            this.createdByEmployeeName = createdBy.fullName;
        }
        var modifiedBy = this.allEmployeeList.find(t => t.employeeId == this.quoteOrder.lastModifiedByEmployeeID);
        if (modifiedBy != null) {
            this.lastModifiedByEmployeeName = modifiedBy.fullName;
        }
    }

    public getQuoteOrderByExternalOrderNumber(externalOrderNumber: string) {
        this.orderService.getQuoteOrderByExternalOrderNumber(externalOrderNumber).subscribe(result => {
            if (result.resultCollection.length > 0) {
                this.quoteOrderID = result.resultCollection[0].quoteOrderId;
                this.navigateQuoteOrder(this.quoteOrderID, false);
                this.setupQuoteOrder(result.resultCollection[0]);
            }
            else {
                this.notificationService.showWarning("Order not found.", "WARNING: External Order # Search");
                console.log("getQuoteOrderByExternalOrderNumber", externalOrderNumber);
            }
        });
    }

    public getQuoteOrder(quoteOrderID: number) {
        console.log("getQuoteOrder", quoteOrderID);
        this.resetAwardOffer();
        this.customerFormGroup.controls["billToCtrl"].setValue("");
        this.searchQuoteOrderLine = {} as SearchQuoteOrderLine;
        if (this.quoteOrder.quoteOrderID != undefined && this.quoteOrder.quoteOrderID > 0 && quoteOrderID != this.quoteOrder.quoteOrderID) {
            this.recordLockAction = "";
            this.recordLockService.clearRecordLock("QuoteOrder", this.quoteOrder.quoteOrderID, this.recordLockSource);
        }
        if (quoteOrderID > 0) {
            this.orderService.getQuoteOrderByID(quoteOrderID).subscribe(result => {
                if (result.resultCollection.length > 0) {
                    this.externalOrderNumber = result.resultCollection[0].externalOrderNumber;
                    this.setupQuoteOrder(result.resultCollection[0]);
                    this.recordLockAction = "viewQuoteOrder";
                    //this.handleRecordLock(quoteOrderID);
                }
                else {
                    this.notificationService.showWarning("Order not found.", "WARNING: Order # Search");
                    console.log("getQuoteOrder", quoteOrderID);
                }
            });
        }
        else {
            this.hasRecordLock = true;
            this.getNewQuoteOrder();
        }
    }

    public getSearchQuoteOrder(quoteOrderID: number) {
        if (quoteOrderID > 0) {
            this.orderService.getSearchQuoteOrderByID(quoteOrderID).subscribe(result => {
                if (result.resultCollection.length > 0) {
                    this.searchQuoteOrder = result.resultCollection[0];
                    this.orderFormType = this.searchQuoteOrder.quoteOrderTypeDescription;
                    if (this.orderFormType.startsWith("RFQ")) {
                        this.orderFormType = "RFQ";
                    }
                    else if (this.orderFormType.startsWith("Quote")) {
                        this.orderFormType = "Quote";
                    }
                    this.payDetailTotal = this.searchQuoteOrder.payDetailTotal;
                    this.thirdPartyPayTotal = this.searchQuoteOrder.thirdPartyPayTotal;
                    this.setupOrderTotals();
                }
                else {
                    this.notificationService.showWarning("Order not found.", "WARNING: Order # Search");
                    console.log("getSearchQuoteOrder", quoteOrderID, result);
                }
            });
        }
    }

    private setupQuoteOrder(order: QuoteOrder) {
        this.quoteOrder = order;
        this.billToID = this.quoteOrder.customerID;
        this.shipperID = this.quoteOrder.shipperId;
        this.consigneeID = this.quoteOrder.consigneeId;
        this.getOpportunityByID(this.quoteOrder.opportunityID);
        //if (!this.opportunity.opportunityId || this.opportunity.opportunityId != this.quoteOrder.opportunityID) {
        //    // Get opportunities if it changed
        //    this.getOpportunityByID(this.quoteOrder.opportunityID);
        //}
        this.getCustomer(this.quoteOrder.customerID, "billto", false);
        this.getCustomer(this.quoteOrder.shipperId, "shipper", false);
        this.getCustomer(this.quoteOrder.consigneeId, "consignee", false);
        //this.getCustomerNotes(this.quoteOrder.customerID, "billto");
        //this.getCustomerNotes(this.quoteOrder.shipperId, "shipper");
        //this.getCustomerNotes(this.quoteOrder.consigneeId, "consignee");
        this.getSearchOrderStops();
        this.getSearchQuoteOrder(this.quoteOrder.quoteOrderID);
        this.getSearchOrderEventSchedule();
        this.isMasterOrderLocked = this.quoteOrder.isMasterOrder;
        //this.isMasterOrderLocked = false;
        var invoiceStatus = this.invoiceStatusList.find(t => t.name == "Prepared");
        if (invoiceStatus != null) {
            this.isPrepared = this.quoteOrder.invoiceStatusID == invoiceStatus.invoiceStatusId;
        }
        this.encryptionService.getEncryptedValue(this.quoteOrder.quoteOrderID.toString()).subscribe(result => {
            this.encryptedOrderNumber = result;
        });
        this.setEmployeeNames();
        this.refreshAllData();
    }

    private getSearchOrderStops() {
        this.orderService.getSearchOrderStopsByQuoteOrderID(this.quoteOrder.quoteOrderID).subscribe(result => {
            this.searchOrderStopList = result.resultCollection;
        })
    }

    private getQuoteOrderLine(quoteOrderLineID: number) {
        console.log("getQuoteOrderLine", quoteOrderLineID, this.quoteOrderLine);
        if (quoteOrderLineID >= 0) {
            this.isEditQuoteOrderLine = true;
            //this.quoteOrderLine = Object.assign({}, this.quoteOrderLineList[idx]);
            this.quoteOrderLine = this.quoteOrderLineList.find(t => t.quoteOrderLineID == quoteOrderLineID)!;
            this.getSearchQuoteOrderLine(quoteOrderLineID);
            this.setMinMaxDates();
            this.pickCityID = this.quoteOrderLine.originCityID;
            this.dropCityID = this.quoteOrderLine.destinationCityID;
            if (this.quoteOrderLine.shipperID > 0) {
                this.getCustomer(this.quoteOrderLine.shipperID, "quoteOrderLineShipper", false);
            }
            if (this.quoteOrderLine.consigneeID > 0) {
                this.getCustomer(this.quoteOrderLine.consigneeID, "quoteOrderLineConsignee", false);
            }
            //this.getCustomerNotes(this.quoteOrderLine.shipperID, "quoteOrderLineShipper");
            //this.getCustomerNotes(this.quoteOrderLine.consigneeID, "quoteOrderLineConsignee");
            this.quoteOrderLineShipperEarliestDate = new Date(this.quoteOrderLine.originalPickupDate);
            this.quoteOrderLineShipperLatestDate = new Date(this.quoteOrderLine.originalPickupEndDate);
            this.quoteOrderLineConsigneeEarliestDate = new Date(this.quoteOrderLine.originalDeliveryDate);
            this.quoteOrderLineConsigneeLatestDate = new Date(this.quoteOrderLine.originalDeliveryEndDate);
            this.getEquipmentClasses();
            this.getTrailerTypes();
            console.log("getQuoteOrderLine", quoteOrderLineID, this.orderFormType, this.quoteOrderLineShipperEarliestDate, this.quoteOrderLineShipperLatestDate, this.quoteOrderLineConsigneeEarliestDate, this.quoteOrderLineConsigneeLatestDate, this.quoteOrderLine);
        }
        else {
            this.utilService.isLoading = true;
            this.isEditQuoteOrderLine = false;
            this.orderService.getQuoteOrderLinesByQuoteOrderID(this.quoteOrder.quoteOrderID).subscribe(result => {
                this.utilService.isLoading = false;
                if (result.resultCollection.length > 0) {
                    this.quoteOrderLineList = result.resultCollection;
                    //this.totalMiles = this.quoteOrderLineList.reduce(function (a, b) {
                    //    return a + b.miles;
                    //}, 0);
                    this.setupOrderTotals();
                    this.quoteOrderLine = result.resultCollection[0];
                    this.getSearchQuoteOrderLine(this.quoteOrderLine.quoteOrderLineID);
                    this.setMinMaxDates();
                    this.pickCityID = this.quoteOrderLine.originCityID;
                    this.dropCityID = this.quoteOrderLine.destinationCityID;
                    if (this.quoteOrderLine.shipperID > 0) {
                        this.getCustomer(this.quoteOrderLine.shipperID, "quoteOrderLineShipper", false);
                    }
                    if (this.quoteOrderLine.consigneeID > 0) {
                        this.getCustomer(this.quoteOrderLine.consigneeID, "quoteOrderLineConsignee", false);
                    }
                    this.quoteOrderLineShipperEarliestDate = new Date(this.quoteOrderLine.originalPickupDate);
                    this.quoteOrderLineShipperLatestDate = new Date(this.quoteOrderLine.originalPickupEndDate);
                    this.quoteOrderLineConsigneeEarliestDate = new Date(this.quoteOrderLine.originalDeliveryDate);
                    this.quoteOrderLineConsigneeLatestDate = new Date(this.quoteOrderLine.originalDeliveryEndDate);
                    this.getEquipmentClasses();
                    this.getTrailerTypes();
                }
                console.log("getQuoteOrderLine:by quoteOrderID", quoteOrderLineID, this.quoteOrderLine, this.quoteOrderLineList);
                //    if (this.quoteOrderLine.quoteOrder != null) this.getOpportunities(this.quoteOrderLine.quoteOrder.customerId, this.quoteOrderLine.quoteOrder.opportunity.productId);
            });
        }
    }

    private getSearchOrderEventSchedule() {
        this.orderService.getSearchOrderEventScheduleByQuoteOrderID(this.quoteOrderID).subscribe(result => {
            this.searchScheduleList = result.resultCollection;
        });
    }

    private setupOrderTotals() {
        if (this.quoteOrderLineList.length < 1) return;

        this.totalMiles = 0;
        this.totalPrice = 0;
        this.totalLinehaulCost = 0;
        this.totalFuelCost = 0;
        this.totalAccessorialCost = 0;
        this.totalCost = 0;

        this.quoteOrderLineList.forEach((item, index) => {
            this.totalMiles = this.totalMiles + item.miles;
            this.totalPrice = this.totalPrice + item.price;
            this.totalLinehaulCost = this.totalLinehaulCost + item.cost;
            this.totalFuelCost = this.totalFuelCost + item.fuelCost;
            this.totalAccessorialCost = this.totalAccessorialCost + item.accessorialCost;
        });

        //this.totalCost = this.totalLinehaulCost + this.totalFuelCost + this.totalAccessorialCost;
        this.totalCost = this.payDetailTotal + this.thirdPartyPayTotal;
    }

    public updateMiles() {
        this.utilService.isLoading = true;
        this.orderService.updateQuoteOrderLineMilesByQuoteOrderID(this.quoteOrder.quoteOrderID).subscribe(result => {
            this.utilService.isLoading = false;
            this.getQuoteOrder(this.quoteOrder.quoteOrderID);
            //console.log("updateMiles", result);
        });
    }

    //private getQuoteOrderHeaders(customerId: number, opportunityId: number) {
    //    this.orderService.getQuoteOrderByCustomerIdOpportunityId(customerId, opportunityId).subscribe(result => {
    //        this.quoteOrders = result;
    //    });
    //}

    public orderStopEventTypeChange(evt: any, type: string) {
        var val: number = evt.target.value;
        var dropTxt: string = this.dropStopEventTypes.find(t => t.orderEventTypeID == val) != null ? this.dropStopEventTypes.find(t => t.orderEventTypeID == val)!.code : "";
        var pickTxt: string = this.pickStopEventTypes.find(t => t.orderEventTypeID == val) != null ? this.pickStopEventTypes.find(t => t.orderEventTypeID == val)!.code : "";
        var stopTxt: string = this.orderStopEventTypes.find(t => t.orderEventTypeID == val) != null ? this.orderStopEventTypes.find(t => t.orderEventTypeID == val)!.code : "";
        var id: number = 0;
        if (type == "drop") {
            this.quoteOrderLineSplit.dropOrderEventCode = dropTxt;
            if (dropTxt == "LUL") {
                id = this.pickStopEventTypes.find(t => t.code == "LLD")!.orderEventTypeID;
                this.quoteOrderLineSplit.pickOrderEventTypeID = id;
                this.quoteOrderLineSplit.pickOrderEventCode = "LLD";
            }
            else if (dropTxt == "DLT") {
                id = this.pickStopEventTypes.find(t => t.code == "HLT")!.orderEventTypeID;
                this.quoteOrderLineSplit.pickOrderEventTypeID = id;
                this.quoteOrderLineSplit.pickOrderEventCode = "HLT";
            }
        }
        else if (type == "pick") {
            this.quoteOrderLineSplit.pickOrderEventCode = pickTxt;
            if (pickTxt == "LLD") {
                id = this.dropStopEventTypes.find(t => t.code == "LUL")!.orderEventTypeID;
                this.quoteOrderLineSplit.dropOrderEventTypeID = id;
                this.quoteOrderLineSplit.dropOrderEventCode = "LUL";
            }
            else if (pickTxt == "HLT") {
                id = this.dropStopEventTypes.find(t => t.code == "DLT")!.orderEventTypeID;
                this.quoteOrderLineSplit.dropOrderEventTypeID = id;
                this.quoteOrderLineSplit.dropOrderEventCode = "DLT";
            }
        }
        else if (type == "stop") {
            this.newOrderStop.eventCode = stopTxt;
            this.newOrderStop.stopType = "";
        }
        //console.log("orderStopEventTypeChange", type, val, dropTxt, pickTxt, this.quoteOrderLineSplit, evt);
    }

    public copyBillTo(customerType: string) {
        //this.shipper = Object.assign({}, this.billTo);
        if (customerType == "shipper") {
            this.onCustomerChange(this.billToID, "shipperCtrl");
        }
        else if (customerType == "consignee") {
            this.onCustomerChange(this.billToID, "consigneeCtrl");
        }
    }

    private getTimeZone(locationType: string) {
        var thisTZ = "";
        if (locationType == "pick") {
            if (this.shipperID > 0) {
                thisTZ = this.shipper.ianaTimeZoneName;
            }
            else if (this.pickCityID > 0) {
                thisTZ = this.pickCity.ianaTimeZoneName;
            }
        }
        else if (locationType == "drop") {
            if (this.consigneeID > 0) {
                thisTZ = this.consignee.ianaTimeZoneName;
            }
            else if (this.dropCityID > 0) {
                thisTZ = this.dropCity.ianaTimeZoneName;
            }
        }
        return thisTZ;
    }

    private setDatesUTC() {
        var pickTZ = this.getTimeZone("pick");
        var dropTZ = this.getTimeZone("drop");
        //if (this.shipperID > 0 && this.consigneeID > 0) {
        //    pickTZ = this.shipper.epiTimeZone.ianaTimeZoneName;
        //    dropTZ = this.consignee.epiTimeZone.ianaTimeZoneName;
        //}
        //else if (this.pickCityID > 0 && this.dropCityID > 0) {
        //    pickTZ = this.pickCity.epiTimeZone.ianaTimeZoneName;
        //    dropTZ = this.dropCity.epiTimeZone.ianaTimeZoneName;
        //}
        if (pickTZ != "" && dropTZ != "" && pickTZ != dropTZ) {
            //var pickDate = this.shipperEarliestDate;
            //var dropDate = this.consigneeEarliestDate;
            this.shipperEarliestDateUTC = this.utilService.getTimeZoneDateAsLocalDate(this.shipperEarliestDate, pickTZ);
            this.shipperLatestDateUTC = this.utilService.getTimeZoneDateAsLocalDate(this.shipperLatestDate, pickTZ);
            this.consigneeEarliestDateUTC = this.utilService.getTimeZoneDateAsLocalDate(this.consigneeEarliestDate, dropTZ);
            this.consigneeLatestDateUTC = this.utilService.getTimeZoneDateAsLocalDate(this.consigneeLatestDate, dropTZ);

            this.shipperEarliestDateUTC = this.utilService.getLocalDateInUTC(this.shipperEarliestDateUTC);
            this.shipperLatestDateUTC = this.utilService.getLocalDateInUTC(this.shipperLatestDateUTC);
            this.consigneeEarliestDateUTC = this.utilService.getLocalDateInUTC(this.consigneeEarliestDateUTC);
            this.consigneeLatestDateUTC = this.utilService.getLocalDateInUTC(this.consigneeLatestDateUTC);
            //if (pickDateUTC <= dropDateUTC) {
            //    dropDateUTC = this.utilService.addMinutes(pickDateUTC, 1);
            //    this.consigneeEarliestDateUTC = this.utilService.getTimeZoneDateAsLocalDate(dropDateUTC, dropTZ);
            //    this.consigneeLatestDateUTC = this.utilService.addMinutes(this.consigneeEarliestDate, 1);
            //}
            //console.log("setDatesUTC", pickTZ, dropTZ, this.shipperEarliestDateUTC, this.consigneeEarliestDateUTC);
        }
    }

    public citySTChanged(evt: any, ctrl: string) {
        var cityID = evt;
        //console.log("citySTChanged", cityID, this.customerCity);
        this.cityService.getSearchCityStateByCityID(cityID).subscribe(result => {
            if (result.resultCollection.length > 0) {
                var city: SearchCityState = result.resultCollection[0];
                if (ctrl == "pickCityCtrl") {
                    this.pickCityID = city.cityID;
                    this.quoteOrderLine.originCityID = this.pickCityID;
                    this.pickCity = city;
                    console.log("citySTChanged:pickCity", cityID, this.pickCityID, this.dropCityID, result);
                }
                else if (ctrl == "dropCityCtrl") {
                    this.dropCityID = city.cityID;
                    this.quoteOrderLine.destinationCityID = this.dropCityID;
                    this.dropCity = city;
                    console.log("citySTChanged:dropCity", cityID, this.pickCityID, this.dropCityID, result);
                }
                this.setDatesUTC();
            }
            else {
                this.notificationService.showWarning("Invalid or missing city.", "WARNING: Invalid City");
            }
        });
    }

    public onCustomerChange(evt: any, ctrl: string) {
        var selectedCustomer = this.customerFormGroup.controls[ctrl].value as CustomerLookup;

        if (ctrl == "billToCtrl") {
            this.billTo = {} as SearchCustomer;
            this.isEditBillTo = false;
            this.isAddBillTo = false;
            if (!isNaN(evt) && evt != null) {
                this.billToID = evt;
            }
            else {
                this.billToID = selectedCustomer.customerID;
            }
            this.getCustomer(this.billToID, "billto", true);
            //this.getCustomerNotes(this.billToID, "billto");
        }
        else if (ctrl == "shipperCtrl") {
            this.shipper = {} as SearchCustomer;
            this.isAddShipper = false;
            this.isEditShipper = false;
            if (!isNaN(evt) && evt != null) {
                this.shipperID = evt;
            }
            else {
                this.shipperID = selectedCustomer.customerID;
            }
            if (this.quoteOrder.quoteOrderID == 0 && this.orderFormType != "Order") {
                if (this.shipperID > 0) {
                    this.pickCityID = 0;
                    this.quoteOrderLine.originCityID = this.pickCityID;
                }
            }
            this.getCustomer(this.shipperID, "shipper", true);
            //this.getCustomerNotes(this.shipperID, "shipper");
        }
        else if (ctrl == "consigneeCtrl") {
            this.consignee = {} as SearchCustomer;
            this.isAddConsignee = false;
            this.isEditConsignee = false;
            if (!isNaN(evt) && evt != null) {
                this.consigneeID = evt;
            }
            else {
                this.consigneeID = selectedCustomer.customerID;
            }
            if (this.quoteOrder.quoteOrderID == 0 && this.orderFormType != "Order") {
                if (this.consigneeID > 0) {
                    this.dropCityID = 0;
                    this.quoteOrderLine.destinationCityID = this.dropCityID;
                }
            }
            this.getCustomer(this.consigneeID, "consignee", true);
            //this.getCustomerNotes(this.consigneeID, "consignee");
        }
        else if (ctrl == "quoteOrderLineShipperCtrl") {
            this.quoteOrderLineShipper = {} as SearchCustomer;
            this.isAddQuoteOrderLineShipper = false;
            this.isEditQuoteOrderLineShipper = false;
            if (!isNaN(evt) && evt != null) {
                this.quoteOrderLine.shipperID = evt;
            }
            else {
                this.quoteOrderLine.shipperID = selectedCustomer.customerID;
            }
            this.getCustomer(this.quoteOrderLine.shipperID, "quoteOrderLineShipper", true);
            //this.getCustomerNotes(this.shipperID, "shipper");
        }
        else if (ctrl == "quoteOrderLineConsigneeCtrl") {
            this.quoteOrderLineConsignee = {} as SearchCustomer;
            this.isAddQuoteOrderLineConsignee = false;
            this.isEditQuoteOrderLineConsignee = false;
            if (!isNaN(evt) && evt != null) {
                this.quoteOrderLine.consigneeID = evt;
            }
            else {
                this.quoteOrderLine.consigneeID = selectedCustomer.customerID;
            }
            this.getCustomer(this.quoteOrderLine.consigneeID, "quoteOrderLineConsignee", true);
            //this.getCustomerNotes(this.consigneeID, "consignee");
        }
        //this.customerID = customerID;
        //this.onCustomerClick.emit(customerID);
        console.log("order-form:onCustomerChange", evt, ctrl, this.billToID, this.shipperID, this.consigneeID);
    }

    private getCustomer(customerID: number, customerType: string, isChange: boolean) {
        this.crmService.getSearchCustomerByID(customerID).subscribe(result => {
            var cust: SearchCustomer = result.resultCollection[0];
            if (customerType == "billto") {
                this.billTo = Object.assign({}, cust);
                if (this.quoteOrder != null && isChange) {
                    this.quoteOrder.customerID = this.billToID;
                    if (this.opportunity.productId > 0) {
                        this.productChanged(this.opportunity.productId);
                        console.log("getCustomer", customerID, this.opportunity.productId, this.quoteOrder.opportunityID);
                    }
                }
            }
            else if (customerType == "shipper") {
                this.shipper = Object.assign({}, cust);
                //console.log("getCustomer:shipper", customerID, result, this.shipper);
                if (this.quoteOrder != null) {
                    this.quoteOrder.shipperId = this.shipperID;
                }
                if (this.quoteOrderLine != null) {
                    this.quoteOrderLine.originCityID = this.shipper.cityID;
                }
            }
            else if (customerType == "consignee") {
                this.consignee = Object.assign({}, cust);
                if (this.quoteOrder != null) {
                    this.quoteOrder.consigneeId = this.consigneeID;
                }
                if (this.quoteOrderLine != null) {
                    this.quoteOrderLine.destinationCityID = this.consignee.cityID;
                }
            }
            else if (customerType == "quoteOrderLineShipper") {
                this.quoteOrderLineShipper = Object.assign({}, cust);
                //if (this.quoteOrder != null) {
                //    this.quoteOrder.shipperId = this.shipperID;
                //}
                if (this.quoteOrderLine != null) {
                    this.quoteOrderLine.originCityID = this.quoteOrderLineShipper.cityID;
                }
                console.log("getCustomer:quoteOrderLineShipper", this.quoteOrderLineShipper, this.quoteOrderLine.originCityID);
            }
            else if (customerType == "quoteOrderLineConsignee") {
                this.quoteOrderLineConsignee = Object.assign({}, cust);
                //if (this.quoteOrder != null) {
                //    this.quoteOrder.consigneeId = this.consigneeID;
                //}
                if (this.quoteOrderLine != null) {
                    this.quoteOrderLine.destinationCityID = this.quoteOrderLineConsignee.cityID;
                }
            }
            //else if (customerType == "orderStop") {
            //    this.orderStopCustomer = Object.assign({}, cust);
            //}
            if (isChange) {
                this.getMileage(customerType);
            }
            this.setDatesUTC();
        });
    }

    public isShowCustomer(customerType: string) {
        var isShow: boolean = true;
        if (customerType == "shipper" && this.shipper.customerID != undefined) {
            if ((this.orderFormType == "Can Get" || this.orderFormType == "Quote") && this.shipper.customerID <= 0) {
                isShow = false;
            }
        }
        if (customerType == "consignee" && this.consignee.customerID != undefined) {
            if ((this.orderFormType == "Can Get" || this.orderFormType == "Quote") && this.consignee.customerID <= 0) {
                isShow = false;
            }
        }
        return isShow;
    }

    private getMileage(customerType: string) {
        if (this.quoteOrderLine.originCityID > 0 && this.quoteOrderLine.destinationCityID > 0) {
            this.azureService.getTravelEstimate(this.quoteOrderLine.originCityID, this.quoteOrderLine.destinationCityID, this.quoteOrderLine.trailerTypeID).subscribe(result => {
                if (result == null) {
                    this.quoteOrderLine.miles = 0;
                }
                else {
                    this.travelEstimate = result;
                    //this.quoteOrderLine.miles = this.quoteOrderLine.roundTrip == true ? this.travelEstimate.miles * 2 : this.travelEstimate.miles;
                    this.quoteOrderLine.miles = this.travelEstimate.miles;
                    if (this.quoteOrderLineList.length <= 1) {
                        this.totalMiles = this.quoteOrderLine.miles;
                    }

                    if (this.travelEstimate.breaks > 0) {
                        var hoursUnit: string = this.travelEstimate.breakHours == 1 ? "hour" : "hours";
                        var breaksUnit: string = this.travelEstimate.breaks == 1 ? "break" : "breaks";
                        this.notificationService.showInfo("NOTE: Travel estimate will include " + this.travelEstimate.breaks + " " + breaksUnit + " for " + this.travelEstimate.breakHours + " " + hoursUnit, "NOTE: Travel Estimate Breaks");
                    }

                    if (customerType == "consignee" || customerType == "quoteOrderLineConsignee") {
                        // Adjust Consignee Earliest/Latest on change of Shipper
                        //var travelTimeMSec: number = this.travelEstimate.seconds * 1000;
                        var travelTimeHr: number = this.travelEstimate.miles / 50;
                        var travelTimeMin: number = travelTimeHr * 60;
                        var travelTimeSec: number = travelTimeMin * 60;
                        var travelTimeMSec: number = travelTimeSec * 1000;
                        //var numRequiredBreaks: number = Math.floor(result / this.maxTravelMilesBeforeBreak);
                        //var breakTimeHours: number = numRequiredBreaks * this.requiredTravelBreakHours;
                        var breakTimeMSec: number = this.travelEstimate.breakHours * 3600 * 1000;
                        var stopOffsetMSec: number = 30 * 60 * 1000;
                        var shipperTime: number = 0;
                        // Add travel time in msec from shipper earliest to consignee earliest
                        // Add 30min offset to consignee latest
                        if (customerType == "consignee") {
                            shipperTime = this.shipperEarliestDate.getTime();
                            this.consigneeEarliestDate = new Date(this.shipperEarliestDate);
                            this.consigneeLatestDate = new Date(this.shipperEarliestDate);
                            this.consigneeEarliestDate.setTime(shipperTime + travelTimeMSec + breakTimeMSec);
                            this.consigneeLatestDate.setTime(shipperTime + travelTimeMSec + breakTimeMSec + stopOffsetMSec);
                        }
                        else {
                            shipperTime = this.quoteOrderLineShipperEarliestDate.getTime();
                            this.quoteOrderLineConsigneeEarliestDate = new Date(this.quoteOrderLineShipperEarliestDate);
                            this.quoteOrderLineConsigneeLatestDate = new Date(this.quoteOrderLineShipperEarliestDate);
                            this.quoteOrderLineConsigneeLatestDate.setTime(shipperTime + travelTimeMSec + breakTimeMSec);
                            this.quoteOrderLineConsigneeLatestDate.setTime(shipperTime + travelTimeMSec + breakTimeMSec + stopOffsetMSec);
                        }
                        this.setDatesUTC();
                        console.log("getMileage", customerType, result, travelTimeMSec, this.consigneeEarliestDate);
                    }
                }
            });
        }
        else {
            this.quoteOrderLine.miles = 0;
        }
    }

    public addOffer(evt: any) {
        var quoteOrderLineID: number = 0;
        if (!isNaN(evt) && evt != null) {
            quoteOrderLineID = evt;
        }
        this.offerQuoteOrderLineID = quoteOrderLineID;
        if (quoteOrderLineID > 0) {
            this.orderService.getQuoteOrderLineById(quoteOrderLineID).subscribe(result => {
                this.isAddOffer = true;
                this.offerQuoteOrderLine = result;
                this.getCustomerCarrierExclusionMap();
                console.log("offer-form:addOffer", evt, this.offerQuoteOrderLine);
            })
        }
    }

    public onCarrierChange(evt: any) {
        //this.carrierOfferBatchID = 0;
        //this.originalQuoteOrderLineID = 0;

        //this.checkCarrierLookupValue();
        //var checkCallCity = this.cityControl.value;
        var selectedCarrier = this.carrierFormGroup.controls['carrierLookupControl'].value as CarrierLookup;

        var carrierID: number = 0;
        if (!isNaN(evt) && evt != null) {
            carrierID = evt;
        }
        else {
            carrierID = selectedCarrier.carrierID;
        }
        this.carrierID = carrierID;
        //this.onCarrierClick.emit(carrierID);
        //console.log("carrier-list-grid:onCarrierChange", this.originalQuoteOrderLineID, evt, selectedCarrier);

        var customerCarriers = this.customerCarrierExclusionMapList.filter(r => r.carrierID == carrierID);
        if (customerCarriers.length > 0) {
            this.isCustomerExcluded = true;
        }
        else {
            this.isCustomerExcluded = false;
        }

        this.utilService.isLoading = true;
        this.cseService.getSearchCarrierByCarrierID(carrierID).subscribe(result => {
            this.utilService.isLoading = false;
            //this.isAddCarrier = false;
            this.carrier = result.resultCollection[0];
            console.log("onCarrierChange", this.carrier, result);
            if (this.carrier != null) {
                this.getCarrierOfferFromCarrier();
            }
        });
        //if (selectedCarrier == null) return;
    }

    private getCarrierOfferFromCarrier() {
        this.editedCarrierList = this.getCarrierListItem();
        this.editedCarrierList.originalQuoteOrderLineID = this.offerQuoteOrderLineID;
        this.editedCarrierList.carrierOfferBatchId = 0;
        this.editedCarrierList.quoteOrderLineId = this.offerQuoteOrderLineID;
        this.editedCarrierList.carrierLaneId = 0;
        this.editedCarrierList.miles = this.offerQuoteOrderLine.miles;
        this.editedCarrierList.offerRate = 0;
        this.editedCarrierList.offerRatePerMile = 0;
        this.editedCarrierList.commentID = 0;
        this.editedCarrierList.commentText = "";
        console.log("getCarrierOfferFromCarrier", this.carrierID, this.editedCarrierList);
    }

    private getCarrierListItem() {
        return {
            quoteOrderLineId: this.offerQuoteOrderLineID,
            carrierId: this.carrier.carrierID,
            carrierCode: this.carrier.carrierCode,
            carrierName: this.carrier.carrierName,
            mcNumber: this.carrier.mcNumber,
            dotNumber: this.carrier.dotNumber,
            scacCode: this.carrier.scacCode,
            originZip3: '',
            destinationZip3: '',
            procurements: 0,
            carrierCity: this.carrier.carrierCity,
            carrierState: this.carrier.carrierState,
            defaultEmail: this.carrier.defaultEmail,
            contactId: this.carrier.contactID,
            modeId: 0,
            trailerTypeId: 0,
            matches: 0,
            miles: this.offerQuoteOrderLine.miles,
            originStateId: 0,
            originMarketId: 0,
            originRegionId: 0,
            destinationStateId: 0,
            destinationMarketId: 0,
            destinationRegionId: 0,
            preferred: false,
            confidenceRanking: 0,
            source: '',
            ratePerMile: 0,
            lastRan: new Date(),
            offerId: 0,
            carrierLaneId: 0,
            lastOfferRate: 0,
            lastOfferDate: new Date(),
            offerRate: 0,
            offerRatePerMile: 0,
            minRate: 0,
            minRateDate: new Date(),
            carrierStatusRowColor: this.carrier.carrierStatusRowColor,
            extendedRate: 0,
            emailed: false,
            declined: false,
            contacted: false,
            awarded: false,
            commentID: 0,
            commentText: "",
            createdByEmployeeID: 0,
            carrierTeam: this.carrier.carrierTeam,
            rmisCertifiedColor: this.carrier.rmisCertifiedColor,
            rmisPotentialCarrierColor: this.carrier.rmisPotentialCarrierColor,
            rmisStatus: this.carrier.rmisStatus,
            nonCertifiedReasons: this.carrier.nonCertifiedReasons,
            rmisStatusColor: this.carrier.rmisStatusColor,
            rmisStatusTextColor: this.carrier.rmisStatusTextColor,
            rmisStatusAbbrev: this.carrier.rmisStatusAbbrev,
            rmisLastPotentialDate: this.carrier.rmisLastPotentialDate,
            isRMISCertified: this.carrier.isRMISCertified,
            carrierIncidents: this.carrier.carrierIncidents
        } as CarrierList;
    }

    private getCustomerCarrierExclusionMap() {
        this.utilService.isLoading = true;
        this.cseService.getCustomerCarrierExclusionMapByCustomerID(this.quoteOrder.customerID).subscribe(result => {
            this.customerCarrierExclusionMapList = result.resultCollection;
            this.utilService.isLoading = false;
        });
    }

    public calculateMargin(type: string) {
        var calc: number = 0;
        if (this.quoteOrder.totalAccessorialCharge != null && this.quoteOrder.totalAccessorialCharge != null && this.quoteOrder.totalAccessorialCharge > 0) {
            calc = (this.quoteOrder.totalAccessorialCharge - this.totalCost) / this.quoteOrder.totalAccessorialCharge;
        }
        return calc;
    }

    public calculateOfferRate(offerRatePerMile: number) {
        if (this.offerQuoteOrderLine.miles > 0) {
            return parseFloat((offerRatePerMile * this.offerQuoteOrderLine.miles).toFixed(2));
        }
        //console.log("calculateRate", offerRatePerMile, this.quoteOrderLine.miles);
    }

    public calculateOfferRatePerMile(offerRate: number) {
        if (this.offerQuoteOrderLine.miles > 0) {
            return parseFloat((offerRate / this.offerQuoteOrderLine.miles).toFixed(2));
        }
        //console.log("calculateRate", offerRate, this.quoteOrderLine.miles);
    }

    public cancelOffer() {
        //this.cancelHandler({ sender: this.carriersGrid, rowIndex: this.editedRowIndex });
        this.offerQuoteOrderLineID = 0;
        this.carrier = {} as SearchCarrier;
        this.editedCarrierList = undefined;
        this.offerQuoteOrderLine = {} as QuoteOrderLine;
        this.isAddOffer = false;
    }

    public saveOffer() {
        this.recordLockAction = "saveOffer";
        this.recordLockService.checkHasRecordLock("QuoteOrder", this.quoteOrderID, this.recordLockSource);
    }

    private allowSaveOffer() {
        this.utilService.isLoading = true;
        //console.log("saveOffer", this.editedCarrierList);
        if (this.editedCarrierList!.carrierLaneId == 0 || this.editedCarrierList!.carrierLaneId == null) {
            this.saveNewCarrierLaneOffer();
        }
        else {
            this.saveCarrierFromGrid(this.editedCarrierList!, true);
            //this.editedCarrierList = undefined;
            //this.onCarrierClick.emit(0);
        }
        console.log("order-form:saveOffer", this.offerQuoteOrderLineID, this.editedCarrierList);
    }

    private saveNewCarrierLaneOffer() {
        console.log("saveNewCarrierLaneOffer", this.offerQuoteOrderLineID, this.editedCarrierList, this.offerQuoteOrderLine);
        this.utilService.isLoading = true;
        this.editedCarrierList!.carrierOfferBatchId = 0;
        this.cseService.getCarrierLaneByCarrierIDGeocodeLaneID(this.editedCarrierList!.carrierId, this.offerQuoteOrderLine.geocodeLaneID, this.offerQuoteOrderLineID).subscribe(result => {
            //console.log("carrierLane", result);
            this.utilService.isLoading = false;
            var lane: CarrierLane = result.resultCollection[0];
            if (lane.procurements == 0) {
                this.editedCarrierList!.source = "ZipToZip";
            }
            this.editedCarrierList!.carrierLaneId = lane.carrierLaneID;
            this.editedCarrierList!.createdByEmployeeID = parseInt(localStorage.getItem('EmployeeId')!);

            //if (this.editedCarrierList!.emailed == true) {
            //    // Emailed selected, so pop up email form an d include default carrier email
            //    this.sendCarrierEmail(this.editedCarrierList!, false);
            //}
            this.utilService.isLoading = true;
            //console.log("saveOfferFromCarrierList", this.editedCarrierList!);
            this.cseService.saveOfferFromCarrierList(this.editedCarrierList!).subscribe(result2 => {
                this.utilService.isLoading = false;
                if (this.isExcludeNonRMISCertified && !this.editedCarrierList!.isRMISCertified && !this.searchQuoteOrderLine.isMexicoOnly && !this.searchQuoteOrderLine.isExcludeRMISCertification) {
                    // Carrier not RMIS certified and app control flag set
                    this.notificationService.showWarning("Carrier is not RMIS certified. Award not available.", "WARNING: Not RMIS Certified");
                }
                else {
                    this.offerID = result2.resultCollection[0].offerId;
                }
                if (result2.resultCollection[0].offerId == 0) {
                    // Offer already exists
                    this.notificationService.showWarning("An offer already exists for this carrier lane.", "WARNING: Offer Exists");
                }
                else {
                    this.editedCarrierList!.offerId = result2.resultCollection[0].offerId;
                    this.notificationService.showSuccess("Offer succesfully saved.", "SUCCESS: Offer Saved");
                }

                this.offerQuoteOrderLineID = 0;
                this.carrier = {} as SearchCarrier;
                this.editedCarrierList = undefined;
                this.offerQuoteOrderLine = {} as QuoteOrderLine;
                this.isAddOffer = false;
            }, error => {
                var ok = error;
                this.utilService.isLoading = false;
            });
        });
    }

    private saveCarrierFromGrid(carrier: CarrierList, isSaveAndEmail: boolean) {
        carrier.originalQuoteOrderLineID = this.offerQuoteOrderLineID;
        console.log("saveCarrierFromGrid", this.offerQuoteOrderLineID, carrier);
        if (carrier.createdByEmployeeID == null || carrier.createdByEmployeeID == 0) {
            carrier.createdByEmployeeID = parseInt(localStorage.getItem('EmployeeId')!);
        }
        //if (isSaveAndEmail && carrier.emailed == true) {
        //    // Emailed selected, so pop up email form and include default carrier email
        //    this.sendCarrierEmail(carrier, false);
        //}
        this.cseService.saveOfferFromCarrierList(carrier as CarrierList).subscribe(result => {
            this.utilService.isLoading = false;
            if (this.isExcludeNonRMISCertified && !carrier.isRMISCertified && !this.searchQuoteOrderLine.isMexicoOnly && !this.searchQuoteOrderLine.isExcludeRMISCertification) {
                // Carrier not RMIS certified and app control flag set
                this.notificationService.showWarning("Carrier is not RMIS certified. Award not available.", "WARNING: Not RMIS Certified");
            }
            else {
                this.offerID = result.resultCollection[0].offerId;
            }
            if (result.resultCollection[0].offerId == 0) {
                // Offer already exists
                this.notificationService.showWarning("An offer already exists for this carrier lane.", "WARNING: Offer Exists");
            }
            else {
                carrier.offerId = result.resultCollection[0].offerId;
                this.notificationService.showSuccess("Offer succesfully saved.", "SUCCESS: Offer Saved");
            }
            this.offerQuoteOrderLineID = 0;
            this.carrier = {} as SearchCarrier;
            this.editedCarrierList = undefined;
            this.offerQuoteOrderLine = {} as QuoteOrderLine;
            this.isAddOffer = false;
            console.log("saveCarrierFromGrid", this.offerQuoteOrderLineID, result);
        }, error => {
            var ok = error;
            this.utilService.isLoading = false;
        });
    }

    public resetAwardOffer() {
        this.offerID = 0;
        this.offerQuoteOrderLineID = 0;
        this.carrier = {} as SearchCarrier;
        this.editedCarrierList = undefined;
        this.offerQuoteOrderLine = {} as QuoteOrderLine;
        this.isAddOffer = false;
    }

    public roundTripChanged() {
        this.quoteOrderLine.miles = this.quoteOrderLine.roundTrip == true ? this.quoteOrderLine.miles * 2 : this.quoteOrderLine.miles / 2;
    }

    public viewTracking() {
        this.router.navigate(['order-tracking', this.encryptedOrderNumber]);
    }

    public emailTracking() {
        var contacts: Contact[] = [];
        var idx = -1;

        if (this.shipper) {
            this.contactService.getContactsByTableIdType(this.shipper.customerID, 'customer').subscribe(result => {
                if (result.length > 0) {
                    idx = contacts.findIndex(c => c.contactType == "Customer" && c.tableID == this.shipper.customerID && c.email != "");
                    if (idx == null || idx < 0) contacts.push(result[0]);
                }
            });
        }

        if (this.consignee) {
            this.contactService.getContactsByTableIdType(this.consignee.customerID, 'customer').subscribe(result => {
                if (result.length > 0) {
                    idx = contacts.findIndex(c => c.contactType == "Customer" && c.tableID == this.consignee.customerID && c.email != "");
                    if (idx == null || idx < 0) contacts.push(result[0]);
                }
            });
        }

        if (this.billTo) {
            this.contactService.getContactsByTableIdType(this.billTo.customerID, 'customer').subscribe(result => {
                if (result.length > 0) {
                    idx = contacts.findIndex(c => c.contactType == "Customer" && c.tableID == this.billTo.customerID && c.email != "");
                    if (idx == null || idx < 0) contacts.push(result[0]);
                }
            });
        }

        var emailTo = "";
        var orderNumber = this.externalOrderNumber != "" && this.externalOrderNumber != null ? this.externalOrderNumber : this.quoteOrder.quoteOrderID.toString();
        var emailSubject = "Tracking for Order #" + orderNumber;
        var trackingUrl = this.baseUrl + "#/order-tracking/" + encodeURIComponent(this.encryptedOrderNumber);
        //console.log(trackingUrl);
        var emailBody = "<a href='" + trackingUrl + "'>" + trackingUrl + "</a>";

        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;
        dialogConfig.data = {
            showEmailBody: false,
            //contacts: contacts,
            emailTo: emailTo,
            emailSubject: emailSubject,
            emailBody: emailBody
        }

        const dialogRef = this.dialog.open(SendEmailDialog, dialogConfig);

        dialogRef.componentInstance.emailTo = emailTo;
        dialogRef.componentInstance.emailSubject = emailSubject;
        dialogRef.componentInstance.emailBody = emailBody;

        dialogRef.afterClosed().subscribe(result => {
            //if (result == null ||
            //    ((result.selectedEmployees == null || result.selectedEmployees.length < 1) &&
            //        (result.selectedGroupId == null || result.selectedGroupId < 1))) return;
            if (result == null ||
                ((result.emailTo == null || result.emailTo.length < 1) &&
                    (result.emailSubject == null || result.emailSubject < 1))) return;

            //var emails = '';
            //var trackingEmail = this.orderPageAppControls.find(a => a.appControl == 'Tracking Alert Email');
            //if (trackingEmail) {
            //    emails += trackingEmail.appSettingValue + '|';
            //}
            //result.selectedEmployees.forEach(e => {
            //    emails += e.email + '|';
            //});

            //this.emailService.sendEmail(emails, result.selectedGroupId, result.notes, true, false, dataItem.eventCode).subscribe(emailResult => {
            //    var ok = emailResult;
            //});
            this.emailService.sendEmail(result.emailTo, "", "", 0, result.emailSubject, result.emailBody).subscribe(emailResult => {
                var ok = emailResult;
                //console.log(result, emailResult);
            });
        });
    }

    public canCopySingleOrder() {
        var ret: boolean = true;
        if (this.orderFormType != "Order" && this.orderFormType != "Can Get") {
            ret = false;
        }
        if (this.quoteOrder.quoteOrderID == 0) {
            ret = false;
        }
        if (this.quoteOrderLineList.length == 0) {
            ret = false;
        }
        return ret;
    }

    public canCopyAsMaster() {
        if (this.quoteOrder.quoteOrderID > 0 && this.searchQuoteOrder.quoteOrderTypeID) {
            //console.log("canCopyAsMaster", this.quoteOrder.shipperId, this.quoteOrder.consigneeId, this.searchQuoteOrder.quoteOrderTypeAbbrev);
            var stopCustomerList = this.searchOrderStopList.filter(t => t.customerID != undefined && t.customerID > 0);
            var isCustomerSet: boolean = false;
            if (stopCustomerList.length == this.searchOrderStopList.length) {
                isCustomerSet = true;
            }
            // if (this.quoteOrder.shipperId > 0 && this.quoteOrder.consigneeId > 0 && this.searchQuoteOrder.quoteOrderTypeAbbrev == "RW") {
            if (isCustomerSet && (this.searchQuoteOrder.quoteOrderTypeAbbrev == "RFQ" || this.searchQuoteOrder.quoteOrderTypeAbbrev == "RFQP" || this.searchQuoteOrder.quoteOrderTypeAbbrev == "RW")) {
                return true;
            }
            else {
                return false;
            }
        }
        else {
            return false;
        }
    }

    public copyAsMaster() {
        this.copyOrder(true);
        this.isCopyOrderAsMaster = true;
    }

    public copyOrder(isSingle: boolean) {
        this.isCopyOrderAsMaster = false;
        if (isSingle) {
            this.utilService.isLoading = true;
            this.orderService.getSearchInvoiceLineByQuoteOrderID(this.quoteOrderID).subscribe(result => {
                this.utilService.isLoading = false;
                var invoiceLineList: SearchInvoiceLine[] = result.resultCollection;
                var lhaul = invoiceLineList.find(t => t.abbrev == "LHaul");
                if (lhaul != null && lhaul.invoiceAmount > 0) {
                    this.isCopyOrder = !isSingle;
                    this.isCopySingleOrder = isSingle;
                    this.quoteOrderCopy.quoteOrderID = this.quoteOrderID;
                    this.quoteOrderCopy.scheduleDescription = this.quoteOrder.description;
                    var today: Date = new Date();
                    var time: Date = new Date(this.quoteOrderLine.originalPickupDate);
                    this.quoteOrderCopy.beginDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), time.getHours(), time.getMinutes(), time.getSeconds());
                    this.quoteOrderCopy.endDate = new Date(this.quoteOrderCopy.beginDate);
                    this.quoteOrderCopy.isMonday = false;
                    this.quoteOrderCopy.isTuesday = false;
                    this.quoteOrderCopy.isWednesday = false;
                    this.quoteOrderCopy.isThursday = false;
                    this.quoteOrderCopy.isFriday = false;
                    this.quoteOrderCopy.isSaturday = false;
                    this.quoteOrderCopy.isSunday = false;
                    this.quoteOrderCopy.employeeID = parseInt(localStorage.getItem('EmployeeId')!);
                }
                else {
                    this.notificationService.showWarning("Invoice Lines Line Haul (LHaul) missing or $0.00.", "WARNING: Copy Order");
                }
            });
        }
        else {
            this.isCopyOrder = !isSingle;
            this.isCopySingleOrder = isSingle;
        }

        //console.log("copyOrder", this.isCopyOrderAsMaster, this.isCopyOrder, this.isCopySingleOrder, this.quoteOrderCopy);
    }

    public saveCopy() {
        console.log("save", this.quoteOrderCopy);
        if (this.isCopySingleOrder) {
            this.quoteOrderCopy.endDate = new Date(this.quoteOrderCopy.beginDate);
        }
        this.quoteOrderCopy.beginDate = this.utilService.getDateAsUTC(this.quoteOrderCopy.beginDate);
        this.quoteOrderCopy.endDate = this.utilService.getDateAsUTC(this.quoteOrderCopy.endDate);
        if (this.validateFields("quoteOrderCopy")) {
            console.log("saveCopy", this.isCopyOrderAsMaster, this.isCopyOrder, this.isCopySingleOrder, this.quoteOrderCopy);
            this.utilService.isLoading = true;
            this.orderService.saveQuoteOrderCopy(this.quoteOrderCopy, this.isCopyOrderAsMaster).subscribe(result => {
                this.utilService.isLoading = false;
                this.isCopySingleOrder = false;
                this.isCopyOrder = false;
                if (result.resultCollection.length > 0) {
                    var order: SearchQuoteOrder = result.resultCollection[0];
                    console.log("order-form:saveCopy", result.resultCollection);
                    if (this.isCopyOrderAsMaster) {
                        this.notificationService.showSuccess("Created master from RFQ.", "SUCCESS: Copy RFQ as Master");
                    }
                    else {
                        this.notificationService.showSuccess("Created order copy.", "SUCCESS: Copy Order");
                    }
                    //this.refreshAllData();
                    this.navigateQuoteOrder(order.quoteOrderID, true);
                } else {
                    this.utilService.isLoading = false;
                    if (this.isCopyOrderAsMaster) {
                        this.notificationService.showError("Failed to create master from RFQ.", "ERROR: Failed to create Master");
                    }
                    else {
                        this.notificationService.showError("Failed to copy order.", "ERROR: Failed to Copy");
                    }
                }
                this.isCopyOrderAsMaster = false;
            });
        }
    }

    public cancelCopy() {
        this.isCopySingleOrder = false;
        this.isCopyOrderAsMaster = false;
    }

    public addCustomer(customerType) {
        if (customerType == "billto") {
            this.isAddBillTo = true;
        }
        if (customerType == "shipper") {
            this.isAddShipper = true;
        }
        if (customerType == "consignee") {
            this.isAddConsignee = true;
        }
        if (customerType == "quoteOrderLineShipper") {
            this.isAddQuoteOrderLineShipper = true;
        }
        if (customerType == "quoteOrderLineConsignee") {
            this.isAddQuoteOrderLineConsignee = true;
        }
    }

    public editCustomer(customerType) {
        if (customerType == "billto") {
            this.isEditBillTo = true;
        }
        if (customerType == "shipper") {
            this.isEditShipper = true;
        }
        if (customerType == "consignee") {
            this.isEditConsignee = true;
        }
        if (customerType == "quoteOrderLineShipper") {
            this.isEditQuoteOrderLineShipper = true;
        }
        if (customerType == "quoteOrderLineConsignee") {
            this.isEditQuoteOrderLineConsignee = true;
        }
    }

    public refreshCustomers(customerID: number, customerType: string) {
        if (customerType == "billto") {
            this.billToID = customerID ? customerID : this.billToID;
            this.isAddBillTo = false;
            this.isEditBillTo = false;
        }
        if (customerType == "shipper") {
            this.shipperID = customerID ? customerID : this.shipperID;
            this.isAddShipper = false;
            this.isEditShipper = false;
        }
        if (customerType == "consignee") {
            this.consigneeID = customerID ? customerID : this.consigneeID;
            this.isAddConsignee = false;
            this.isEditConsignee = false;
        }
        if (customerType == "quoteOrderLineShipper") {
            this.quoteOrderLine.shipperID = customerID ? customerID : this.quoteOrderLine.shipperID;
            this.isAddQuoteOrderLineShipper = false;
            this.isEditQuoteOrderLineShipper = false;
        }
        if (customerType == "quoteOrderLineConsignee") {
            this.quoteOrderLine.consigneeID = customerID ? customerID : this.quoteOrderLine.consigneeID;
            this.isAddQuoteOrderLineConsignee = false;
            this.isEditQuoteOrderLineConsignee = false;
        }
        if (customerID) {
            this.getCustomer(customerID, customerType, false);
        }
        console.log("refreshCustomers", customerID, customerType);
    }

    public editCarrierReps(evt: any) {
        this.csrQuoteOrderLineID = evt.target ? evt.target.value : evt;
        if (this.csrQuoteOrderLineID > 0) {
            this.isEditCarrierReps = true;
        }
    }

    public editCarrierServices(evt: any) {
        this.carrierServiceQuoteOrderLineID = evt.target ? evt.target.value : evt;
        if (this.carrierServiceQuoteOrderLineID > 0) {
            this.isEditCarrierServices = true;
        }
    }

    public setLoadTags(evt: any) {
        this.isSetLoadTags = false;
        this.tagsQuoteOrderLineID = evt.target ? evt.target.value : evt;
        if (this.tagsQuoteOrderLineID > 0) {
            this.isSetLoadTags = true;
        }
    }

    public showMapPoint(lat: number, lon: number) {
        //this.notificationService.showWarning("Customer map not yet implmented.", "Not Implemented.");
        //var url: string = "https://bing.com/maps/default.aspx?cp=" + lat + "~" + lon;
        var url: string = "https://bing.com/maps/default.aspx?rtp=pos." + lat + "_" + lon + "&rtop=0~1~0";
        //https://bing.com/maps/default.aspx?cp=47.677797~-122.122013
        //https://bing.com/maps/default.aspx?rtp=adr.One%20Microsoft%20Way,%20Redmond,%20WA%2098052
        //https://bing.com/maps/default.aspx?rtp=pos.45.23423_-122.1232&rtop=0~1~0
        return url;
    }

    public refreshData(evt: any) {
        this.refreshLoadSplit();
        if (evt == "loads") {
            this.isEditCarrierReps = false;
            this.isEditCarrierServices = false;
            this.csrQuoteOrderLineID = 0;
            this.carrierServiceQuoteOrderLineID = 0;
            this.isSetLoadTags = false;
            this.tagsQuoteOrderLineID = 0;
            this.loadsRefreshDate = new Date();
        }
        else if (evt == "stops") {
            //this.isAddStop = false;
            //this.isEditStop = false;
            this.stopsRefreshDate = new Date();
        }
        else if (evt == "tags") {
            this.tagsRefreshDate = new Date();
        }
    }

    public refreshAllData() {
        this.getQuoteOrderLine(-1);
        //this.getQuoteOrder(this.quoteOrder.quoteOrderID);
        this.refreshData("loads");
        this.refreshData("stops");
    }

    private setMinMaxDates() {
        this.minStopDate = this.quoteOrderLine.originalPickupDate;
        this.maxStopDate = this.quoteOrderLine.originalDeliveryDate;
    }

    public getUTCDate(strDate: string) {
        if (strDate != null) {
            if (strDate.indexOf("Z") > 0) {
                return strDate;
            }
            else {
                return strDate + "Z";
            }
        }
        return strDate;
    }

    public dateChanged(source: string, field: string) {
        //this.utilService.getDateAsUTC(
        //console.log("dateChanged:local", source, field, this.shipperEarliestDate, this.shipperLatestDate, this.consigneeEarliestDate, this.consigneeLatestDate);
        //console.log("dateChanged:UTC", source, field, this.utilService.getDateAsUTC(this.shipperEarliestDate), this.utilService.getDateAsUTC(this.shipperLatestDate), this.utilService.getDateAsUTC(this.consigneeEarliestDate), this.utilService.getDateAsUTC(this.consigneeLatestDate));
        this.setDatesUTC();
        console.log("dateChanged", this.shipperEarliestDate, this.shipperLatestDate, this.consigneeEarliestDate, this.consigneeLatestDate);
        if (source == "shipper" || source == "consignee") {
            this.isShipperLatestValid = true;
            this.isShipperEarliestValid = true;
            this.isConsigneeEarliestValid = true;
            this.isConsigneeLatestValid = true;

            if (this.shipperEarliestDate > this.shipperLatestDate) {
                if (field == "earliest") {
                    this.shipperLatestDate = this.utilService.addMinutes(new Date(this.shipperEarliestDate), 30);
                }
                else {
                    this.isShipperEarliestValid = false;
                    this.isShipperLatestValid = false;
                }
            }
            if (this.shipperEarliestDateUTC >= this.consigneeEarliestDateUTC) {
                if (source == "shipper" && field == "earliest") {
                    this.consigneeEarliestDateUTC = new Date(this.shipperEarliestDateUTC);
                    this.consigneeEarliestDateUTC.setTime(this.consigneeEarliestDateUTC.getTime() + (60 * 60 * 1000));
                    this.consigneeLatestDateUTC = new Date(this.shipperEarliestDateUTC);

                    this.consigneeEarliestDate = this.utilService.getTimeZoneDateAsLocalDate(this.consigneeEarliestDateUTC, "UTC");
                    this.consigneeLatestDate = this.utilService.getTimeZoneDateAsLocalDate(this.consigneeLatestDateUTC, "UTC");
                    var dropTZ = this.getTimeZone("drop");
                    if (dropTZ != "") {
                        this.consigneeEarliestDate = this.utilService.getLocalDateInTimeZone(this.consigneeEarliestDate, dropTZ);
                        this.consigneeLatestDate = this.utilService.getLocalDateInTimeZone(this.consigneeLatestDate, dropTZ);
                    }

                    //this.consigneeEarliestDate = new Date(this.shipperEarliestDate);
                    //this.consigneeEarliestDate.setTime(this.consigneeEarliestDate.getTime() + (60 * 60 * 1000));
                    //this.consigneeLatestDate = new Date(this.shipperEarliestDate);
                }
                else {
                    this.isShipperEarliestValid = false;
                }
            }
            if (this.minStopDate && this.shipperEarliestDate <= this.minStopDate
                || this.maxStopDate && this.shipperEarliestDate >= this.maxStopDate) {
                this.isShipperEarliestValid = false;
            }

            if (this.consigneeEarliestDate > this.consigneeLatestDate) {
                if (field == "earliest") {
                    this.consigneeLatestDate = this.utilService.addMinutes(new Date(this.consigneeEarliestDate), 30);
                }
                else {
                    this.isConsigneeEarliestValid = false;
                    this.isConsigneeLatestValid = false;
                }
            }
            if (this.consigneeEarliestDateUTC <= this.shipperEarliestDateUTC) {
                this.isConsigneeEarliestValid = false;
            }
            if (this.minStopDate && this.consigneeEarliestDate <= this.minStopDate
                || this.maxStopDate && this.consigneeEarliestDate >= this.maxStopDate) {
                this.isConsigneeEarliestValid = false;
            }
        }
        else if (source == "quoteOrderLineShipper" || source == "quoteOrderLineConsignee") {
            this.isQuoteOrderLineShipperLatestValid = true;
            this.isQuoteOrderLineShipperEarliestValid = true;
            this.isQuoteOrderLineConsigneeEarliestValid = true;
            this.isQuoteOrderLineConsigneeLatestValid = true;

            // Split Shipper (shown as Drop Off)
            if (this.quoteOrderLineShipperEarliestDate > this.quoteOrderLineShipperLatestDate) {
                if (field == "earliest") {
                    this.quoteOrderLineShipperLatestDate = new Date(this.quoteOrderLineShipperEarliestDate);
                }
                else {
                    this.isQuoteOrderLineShipperEarliestValid = false;
                    this.isQuoteOrderLineShipperLatestValid = false;
                }
            }
            if (this.quoteOrderLineShipperEarliestDate >= this.quoteOrderLineConsigneeEarliestDate) {
                if (source == "quoteOrderLineShipper" && field == "earliest") {
                    this.quoteOrderLineConsigneeEarliestDate = new Date(this.quoteOrderLineShipperEarliestDate);
                    this.quoteOrderLineConsigneeEarliestDate.setTime(this.quoteOrderLineConsigneeEarliestDate.getTime() + (60 * 60 * 1000));
                    this.quoteOrderLineConsigneeLatestDate = new Date(this.quoteOrderLineShipperEarliestDate);
                }
                else {
                    this.isQuoteOrderLineShipperEarliestValid = false;
                }
            }
            if (this.minStopDate && this.quoteOrderLineShipperEarliestDate <= this.minStopDate
                || this.maxStopDate && this.quoteOrderLineShipperEarliestDate >= this.maxStopDate) {
                this.isQuoteOrderLineShipperEarliestValid = false;
            }

            // Split Consignee (shown as Pick Up)
            if (this.quoteOrderLineConsigneeEarliestDate > this.quoteOrderLineConsigneeLatestDate) {
                if (field == "earliest") {
                    this.quoteOrderLineConsigneeLatestDate = new Date(this.quoteOrderLineConsigneeEarliestDate);
                }
                else {
                    this.isQuoteOrderLineConsigneeEarliestValid = false;
                    this.isQuoteOrderLineConsigneeLatestValid = false;
                }
            }
            if (this.quoteOrderLineConsigneeEarliestDate <= this.quoteOrderLineShipperEarliestDate) {
                this.isQuoteOrderLineConsigneeEarliestValid = false;
            }
            if (this.minStopDate && this.quoteOrderLineConsigneeEarliestDate <= this.minStopDate
                || this.maxStopDate && this.quoteOrderLineConsigneeEarliestDate >= this.maxStopDate) {
                this.isQuoteOrderLineConsigneeEarliestValid = false;
            }
        }
        else if (source == "copyOrderBeginDate") {
            this.isCopyOrderBeginDateValid = true;
            this.quoteOrderCopy.endDate = new Date(this.quoteOrderCopy.beginDate);
        }
        this.setDatesUTC();
    }

    public productChanged(evt: any) {
        var productID = evt.target ? evt.target.value : evt;
        var product = this.productList.find(t => t.productId == productID);
        if (product != null && product.defaultRateIndexId != null) {
            this.quoteOrder.rateIndexId = product.defaultRateIndexId;
        }

        console.log("productChanged", productID, product, this.quoteOrder.rateIndexId);

        // Reset opportunity before loading dropdown in case Save clicked
        //this.quoteOrder.opportunityID = -1;
        this.opportunityList = [];
        this.getOpportunities(this.billToID, productID, product!.productName);
    }

    public setExpedite() {
        if (this.isExpedite) {
            var rateIndex = this.rateIndexList.find(t => t.description == "Expedite");
            this.quoteOrder.rateIndexId = rateIndex!.rateIndexID;
            //this.rateIndexChanged(rateIndex!.rateIndexID);
        }
        else {
            this.productChanged(this.opportunity.productId);
        }
        console.log("setExpedite", this.quoteOrder.rateIndexId);
    }

    public rateIndexChanged(evt: any) {
        var rateIndexID = evt.target ? evt.target.value : evt;

        if (rateIndexID > 0) {
            var rateIndex = this.rateIndexList.find(t => t.rateIndexID == rateIndexID);
            if (rateIndex != null) {
                if (rateIndex.description == "Expedite") {
                    var highPriority = this.quoteOrderLinePriorityList.find(t => t.abbrev == "High");
                    if (highPriority != null) {
                        this.quoteOrderLine.quoteOrderLinePriorityID = highPriority.quoteOrderLinePriorityID;
                    }
                }
            }
        }
    }

    public modeChanged(evt: any) {
        var modeID = evt.target ? evt.target.value : evt;

        if (modeID > 0) {
            var mode = this.modeList.filter(t => t.modeID == modeID);
            if (mode != null && mode.length > 0) {
                var trailerType = this.trailerTypeList.filter(t => t.trailerTypeID == mode[0].defaultTrailerTypeId);

                if (trailerType != null && trailerType.length > 0) {
                    this.quoteOrderLine.trailerTypeID = trailerType[0].trailerTypeID;
                    this.quoteOrderLine.trailerLength = trailerType[0].defaultLength;
                }
            }
            else {
                this.quoteOrderLine.trailerTypeID = 0;
            }
        } else {
            this.quoteOrderLine.trailerTypeID = 0;
        }

        this.quoteOrderLine.equipmentClassID = -1;
        this.quoteOrderLine.trailerTypeID = -1;
        this.equipmentClassList.length = 0;
        this.trailerTypeList.length = 0;

        this.getEquipmentClasses();
        this.getTrailerTypes();
    }

    public equipmentClassChanged(evt: any) {
        var classID = evt.target ? evt.target.value : evt;

        if (classID > 0) {
            var equipmentClass = this.equipmentClassList.filter(t => t.equipmentClassID == classID);
            if (equipmentClass != null && equipmentClass.length > 0) {
                //var trailerType = this.trailerTypeList.filter(t => t.trailerTypeID == equipmentClass[0].default);

                //    if (trailerType != null && trailerType.length > 0) {
                //        this.quoteOrderLine.trailerTypeID = trailerType[0].trailerTypeID;
                //        this.quoteOrderLine.trailerLength = trailerType[0].defaultLength;
                //    }
            }
            else {
                this.quoteOrderLine.equipmentClassID = 0;
            }
        } else {
            this.quoteOrderLine.equipmentClassID = 0;
        }

        this.quoteOrderLine.trailerTypeID = -1;
        this.trailerTypeList.length = 0;

        this.getTrailerTypes();
    }

    public trailerTypeChanged(evt: any) {
        var trailerTypeID = evt.target.value;

        if (trailerTypeID > 0) {
            var trailerType = this.trailerTypeList.find(t => t.trailerTypeID == trailerTypeID);
            if (trailerType != null) {
                //if (trailerType.abbrev == "VM") {
                //    var highPriority = this.quoteOrderLinePriorityList.find(t => t.abbrev == "High");
                //    if (highPriority != null) {
                //        this.quoteOrderLine.quoteOrderLinePriorityID = highPriority.quoteOrderLinePriorityID;
                //    }
                //}
                this.quoteOrderLine.trailerLength = trailerType.defaultLength;
            }
        }
    }

    private validateFields(formType: string) {
        var isValid: boolean = true;
        var errList: string[] = [];
        if (formType == "order") {
            if (this.quoteOrder.customerID == null || this.quoteOrder.customerID == 0) {
                isValid = false;
                errList.push("Bill To");
            }
            if (this.orderFormType == "Order") {
                if (this.quoteOrder.shipperId == null || this.quoteOrder.shipperId == 0) {
                    isValid = false;
                    errList.push("Shipper");
                }
                if (this.quoteOrder.consigneeId == null || this.quoteOrder.consigneeId == 0) {
                    isValid = false;
                    errList.push("Consignee");
                }
            }
            else {
                if ((this.quoteOrder.shipperId == null || this.quoteOrder.shipperId == 0) && (this.quoteOrderLine.originCityID == null || this.quoteOrderLine.originCityID == 0)) {
                    isValid = false;
                    errList.push("Shipper");
                    errList.push("Pick City");
                }
                if ((this.quoteOrder.consigneeId == null || this.quoteOrder.consigneeId == 0) && (this.quoteOrderLine.destinationCityID == null || this.quoteOrderLine.destinationCityID == 0)) {
                    isValid = false;
                    errList.push("Consignee");
                    errList.push("Drop City");
                }
            }
            if (this.quoteOrder.opportunityID == null || this.quoteOrder.opportunityID <= 0) {
                isValid = false;
                errList.push("Opportunity");
            }
            if (this.quoteOrder.rateIndexId == null || this.quoteOrder.rateIndexId == 0) {
                isValid = false;
                errList.push("Rate Index");
            }
            if (this.quoteOrderLine.modeID == null || this.quoteOrderLine.modeID == 0) {
                isValid = false;
                errList.push("Mode");
            }
            if (this.quoteOrderLine.equipmentClassID == null || this.quoteOrderLine.equipmentClassID == 0) {
                isValid = false;
                errList.push("Equipment Class");
            }
            if (this.quoteOrderLine.trailerTypeID == null || this.quoteOrderLine.trailerTypeID == 0) {
                isValid = false;
                errList.push("Trailer Type");
            }
            if (this.quoteOrderLine.quoteOrderLinePriorityID == null || this.quoteOrderLine.quoteOrderLinePriorityID == 0) {
                isValid = false;
                errList.push("Priority");
            }
            if (!this.isShipperEarliestValid || !this.isShipperLatestValid
                || !this.isConsigneeEarliestValid || !this.isConsigneeLatestValid) {
                isValid = false;
                errList.push("Shipper/Consignee Earliest/Latest");
            }
        }
        else if (formType == "quoteorderline") {
            if ((this.quoteOrderLine.shipperID == null || this.quoteOrderLine.shipperID == 0) && (this.quoteOrderLine.originCityID == null || this.quoteOrderLine.originCityID == 0)) {
                isValid = false;
            }
            if ((this.quoteOrderLine.consigneeID == null || this.quoteOrderLine.consigneeID == 0) && (this.quoteOrderLine.destinationCityID == null || this.quoteOrderLine.destinationCityID == 0)) {
                isValid = false;
            }
            if (this.quoteOrderLine.modeID == null || this.quoteOrderLine.modeID == 0) {
                isValid = false;
            }
            if (this.quoteOrderLine.equipmentClassID == null || this.quoteOrderLine.equipmentClassID == 0) {
                isValid = false;
            }
            if (this.quoteOrderLine.trailerTypeID == null || this.quoteOrderLine.trailerTypeID == 0) {
                isValid = false;
            }
            if (this.quoteOrderLine.quoteOrderLinePriorityID == null || this.quoteOrderLine.quoteOrderLinePriorityID == 0) {
                isValid = false;
            }
        }
        else if (formType == "split") {
            if ((this.quoteOrderLine.shipperID == null || this.quoteOrderLine.shipperID == 0) && (this.quoteOrderLine.originCityID == null || this.quoteOrderLine.originCityID == 0)) {
                isValid = false;
                errList.push("Shipper or Origin City");
            }
            if ((this.quoteOrderLine.consigneeID == null || this.quoteOrderLine.consigneeID == 0) && (this.quoteOrderLine.destinationCityID == null || this.quoteOrderLine.destinationCityID == 0)) {
                isValid = false;
                errList.push("Consignee or Destination City");
            }
            if (this.quoteOrderLineSplit.dropOrderEventTypeID == null || this.quoteOrderLineSplit.dropOrderEventTypeID == 0) {
                isValid = false;
                errList.push("Drop Event Type");
            }
            if (this.quoteOrderLineSplit.dropOrderEventCode == null || this.quoteOrderLineSplit.dropOrderEventCode == "") {
                isValid = false;
                errList.push("Drop Event Code");
            }
            if (this.quoteOrderLineSplit.pickOrderEventTypeID == null || this.quoteOrderLineSplit.pickOrderEventTypeID == 0) {
                isValid = false;
                errList.push("Pick Event Type");
            }
            if (this.quoteOrderLineSplit.pickOrderEventCode == null || this.quoteOrderLineSplit.pickOrderEventCode == "") {
                isValid = false;
                errList.push("Pick Event Code");
            }
            if (this.quoteOrder.rateIndexId == null || this.quoteOrder.rateIndexId == 0) {
                isValid = false;
                errList.push("Rate Index");
            }
            if (this.quoteOrderLine.modeID == null || this.quoteOrderLine.modeID == 0) {
                isValid = false;
                errList.push("Mode");
            }
            if (this.quoteOrderLine.trailerTypeID == null || this.quoteOrderLine.trailerTypeID == 0) {
                isValid = false;
                errList.push("Trailer Type");
            }
            if (this.quoteOrderLine.quoteOrderLinePriorityID == null || this.quoteOrderLine.quoteOrderLinePriorityID == 0) {
                isValid = false;
                errList.push("Priority");
            }
            if (!this.isQuoteOrderLineShipperEarliestValid) {
                isValid = false;
                errList.push("Drop Earliest Date");
            }
            if (!this.isQuoteOrderLineShipperLatestValid) {
                isValid = false;
                errList.push("Drop Latest Date");
            }
            if (!this.isQuoteOrderLineConsigneeEarliestValid) {
                isValid = false;
                errList.push("Pick Earliest Date");
            }
            if (!this.isQuoteOrderLineConsigneeLatestValid) {
                isValid = false;
                errList.push("Pick Latest Date");
            }
        }
        else if (formType == "quoteOrderCopy") {
            if (!this.isCopyOrderBeginDateValid) {
                isValid = false;
            }
        }
        if (!isValid) {
            this.notificationService.showWarning("Please correct required fields", "WARNING: Fields Required");
            console.log("isValid", errList);
        }
        console.log("isValid", errList);
        return isValid;
    }

    public saveOrder() {
        if (this.quoteOrder.quoteOrderID > 0) {
            this.recordLockAction = "saveOrder";
            this.recordLockService.checkHasRecordLock("QuoteOrder", this.quoteOrder.quoteOrderID, this.recordLockSource);
        }
        else {
            this.allowSaveOrder();
        }
    }

    private allowSaveOrder() {
        if (this.validateFields("order")) {
            this.utilService.isLoading = true;

            var employeeId = localStorage.getItem('EmployeeId') == null ? -1 : parseInt(localStorage.getItem('EmployeeId')!);
            if (this.quoteOrder.quoteOrderID < 1) {
                this.quoteOrder.createdByEmployeeID = employeeId;
            }
            this.quoteOrder.lastModifiedByEmployeeID = employeeId;

            if (this.quoteOrder.quoteOrderID == 0 && this.orderFormType != "Order") {
                if (this.quoteOrder.shipperId > 0) {
                    this.pickCityID = 0;
                    this.quoteOrderLine.originCityID = this.pickCityID;
                }
                if (this.quoteOrder.consigneeId > 0) {
                    this.dropCityID = 0;
                    this.quoteOrderLine.destinationCityID = this.dropCityID;
                }
            }

            console.log("saveOrder", "quoteOrder", this.quoteOrder, "quoteOrderLine", this.quoteOrderLine);

            var isNew = this.quoteOrder.quoteOrderID > 0 ? false : true;

            this.utilService.setAuditNotes("Order Entry: Save Add Order");
            this.orderService.saveQuoteOrder(this.quoteOrder).subscribe(result => {
                this.utilService.isLoading = false;
                if (result.resultCollection.length > 0) {
                    var updatedOrder: QuoteOrder = result.resultCollection[0];
                    console.log("saveOrder", "updatedOrder", updatedOrder);
                    if (updatedOrder.quoteOrderID > 0) {
                        this.quoteOrderID = updatedOrder.quoteOrderID;
                        this.quoteOrder = Object.assign({}, updatedOrder);
                        this.setEmployeeNames();
                        if (isNew) {
                            this.getNewQuoteOrderLine(0);
                            this.utilService.setAuditNotes("Order Entry: Save Add Load");
                            this.orderService.saveQuoteOrderLine(this.quoteOrderLine).subscribe(result2 => {
                                if (result2 != null) {
                                    this.quoteOrderID = updatedOrder.quoteOrderID;
                                    this.quoteOrderLine = Object.assign({}, result2);
                                    if (this.shipperHasAppointment || this.consigneeHasAppointment) {
                                        this.utilService.setAuditNotes("Order Entry: Save Has Appointment");
                                        this.orderService.saveOrderEntryHasAppointment(this.quoteOrderID, this.shipperHasAppointment, this.consigneeHasAppointment).subscribe(result3 => {
                                            this.saveOrderRefresh(result2);
                                        });
                                    }
                                    else {
                                        this.saveOrderRefresh(result2);
                                    }
                                }
                                else {
                                    this.notificationService.showError("Error saving Load", "ERROR: Save Load");
                                }
                            });
                        }
                    }
                    else {
                        this.notificationService.showError("Error saving Order (not saved)", "ERROR: Save Order");
                    }
                }
                else {
                    this.notificationService.showError("Error saving Order (no results)", "ERROR: Save Order");
                }
            });
        }
    }

    private saveOrderRefresh(newLine: QuoteOrderLine) {
        this.setMinMaxDates();
        this.quoteOrderLineList.length = 0;
        this.shipperHasAppointment = false;
        this.consigneeHasAppointment = false;
        this.quoteOrderLineList.push(newLine);
        this.notificationService.showSuccess("Created Order " + this.quoteOrder.quoteOrderID + "-" + this.quoteOrderLine.legNumber + ".", "SUCCESS: Save Order");
        this.navigateQuoteOrder(this.quoteOrder.quoteOrderID, true);
        console.log("saveOrder", "quoteOrderLine", this.quoteOrderLine);
    }

    public changeMasterOrderLock() {
        this.isMasterOrderLocked = !this.isMasterOrderLocked;
    }

    public masterOrderChanged() {
        this.utilService.isLoading = true;
        this.orderService.saveQuoteOrder(this.quoteOrder).subscribe(result => {
            this.utilService.isLoading = false;
            if (result.resultCollection.length > 0) {
                var updatedOrder: QuoteOrder = result.resultCollection[0];
                console.log("masterOrderChanged", updatedOrder);
                this.notificationService.showSuccess("Master Order updated.", "SUCCESS: Save Order");
            }
            else {
                this.notificationService.showError("Error saving Order", "ERROR: Save Order");
            }
        });
    }

    public refreshOrderLoadStatus() {
        this.orderService.refreshOrderLoadStatus(this.quoteOrderID).subscribe(result => {
            console.log("refreshOrderLoadStatus", result);
        });
    }

    public exportOrder() {
        if (this.quoteOrder.isExternalOrder != true && (this.quoteOrder.externalOrderNumber == null || this.quoteOrder.externalOrderNumber == "")) {
            this.utilService.isLoading = true;
            this.tmwService.exportTMWOrder(this.quoteOrderLineList[0].quoteOrderLineID).subscribe(result => {
                this.utilService.isLoading = false;
                console.log("exportTMWOrder", result);
                this.notificationService.showSuccess("Order Successfully Exported", "SUCCESS: Export Order");
                this.getQuoteOrder(this.quoteOrderID);
            });
        }
    }

    public syncOrder() {
        if (this.quoteOrder.isExternalOrder == true && this.quoteOrder.externalOrderNumber != null && this.quoteOrder.externalOrderNumber != "") {
            this.recordLockAction = "syncOrder";
            this.recordLockService.checkHasRecordLock("QuoteOrder", this.quoteOrder.quoteOrderID, this.recordLockSource);
        }
    }

    private allowSyncOrder() {
        if (this.quoteOrder.isExternalOrder == true && this.quoteOrder.externalOrderNumber != null && this.quoteOrder.externalOrderNumber != "") {
            this.utilService.isLoading = true;
            this.orderService.syncOrder(this.quoteOrder.externalOrderNumber).subscribe(result => {
                this.utilService.isLoading = false;
                console.log("syncOrder", result);
                this.notificationService.showSuccess("Order Successfully Synced", "SUCCESS: Sync Order");
                this.getQuoteOrder(this.quoteOrderID);
            });
        }
    }

    public sendEmail() {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;
        dialogConfig.data = {
            showNotes: true,
            showCheckCallNotes: false,
            showSystemNotes: false
        }

        const dialogRef = this.dialog.open(EmployeeMultiselectDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(result => {
            if (result == null ||
                ((result.selectedEmployees == null || result.selectedEmployees.length < 1) &&
                    (result.selectedGroupId == null || result.selectedGroupId < 1))) return;

            var emails = '';
            if (result.selectedEmployees != null) {
                result.selectedEmployees.forEach(e => {
                    emails += e.email + '|';
                });
            }

            if (result.selectedEmails != null) {
                emails += result.selectedEmails + '|';
            }

            var orderNumber = (this.quoteOrder.externalOrderNumber != null && this.quoteOrder.externalOrderNumber != "" ? this.quoteOrder.externalOrderNumber : this.quoteOrder.quoteOrderID.toString());
            this.orderService.sendQuoteOrderEmail(this.quoteOrder.quoteOrderID, orderNumber, emails, result.selectedGroupId, "", result.notes, 0, "", false, false, "", false).subscribe(emailResult => {
                var ok = emailResult;
            });
        });
    }

    public setQuoteOrderType(orderType: string) {
        this.setQuoteOrderTypeValue = orderType;
        this.recordLockAction = "setQuoteOrderType";
        this.recordLockService.checkHasRecordLock("QuoteOrder", this.quoteOrder.quoteOrderID, this.recordLockSource);
    }

    private allowSetQuoteOrderType(orderType: string) {
        this.setQuoteOrderTypeValue = "";
        //console.log("setQuoteOrderLineLoadStatus", line.quoteOrderLineID, line);
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;
        dialogConfig.data = {
            header: "Convert From " + orderType + " to Order?",
            message: "You are about to convert this from " + orderType + " to Order.  Confirm to continue."
        }

        const dialogRef = this.dialog.open(ConfirmDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(dialogResult => {
            if (!dialogResult) return;

            this.utilService.isLoading = true;
            var quoteOrderType: QuoteOrderType = this.quoteOrderTypeList.find(t => t.abbrev == "ORD")!;
            var employee = parseInt(localStorage.getItem('EmployeeId')!);
            this.orderService.setQuoteOrderType(this.quoteOrder.quoteOrderID, quoteOrderType.quoteOrderTypeID, employee).subscribe(result => {
                this.utilService.isLoading = false;
                if (result.message != "OK") {
                    this.notificationService.showError("Error converting " + orderType + " to Order: " + result.message, "ERROR: Convert " + orderType + " to Order");
                }
                else {
                    this.notificationService.showSuccess("Successfully converted " + orderType + " to Order", "SUCCESS: Convert " + orderType + " to Order");
                    console.log("SetQuoteOrderType", this.quoteOrder);
                }
                this.getQuoteOrder(this.quoteOrderID);
            });
        });
    }

    public cancelOrder() {
        this.recordLockAction = "cancelOrder";
        this.recordLockService.checkHasRecordLock("QuoteOrder", this.quoteOrder.quoteOrderID, this.recordLockSource);
    }

    private allowCancelOrder() {
        //console.log("setQuoteOrderLineLoadStatus", line.quoteOrderLineID, line);
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;
        dialogConfig.data = {
            header: 'Cancel Order?',
            message: 'You are about to cancel this order.  Confirm to continue.'
        }

        const dialogRef = this.dialog.open(ConfirmDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(dialogResult => {
            if (!dialogResult) return;

            this.utilService.isLoading = true;
            this.orderService.cancelOrder(this.quoteOrder.quoteOrderID).subscribe(result => {
                this.utilService.isLoading = false;
                if (result.message != "OK") {
                    this.notificationService.showError("Error canceling order: " + result.message, "ERROR: Cancel Order");
                }
                else {
                    this.notificationService.showSuccess("Successfully canceled order", "SUCCESS: Cancel Order");
                    console.log("cancelOrder", this.quoteOrder);
                }
                this.getQuoteOrder(this.quoteOrderID);
            });
        });
    }

    public unCancelOrder() {
        this.recordLockAction = "unCancelOrder";
        this.recordLockService.checkHasRecordLock("QuoteOrder", this.quoteOrder.quoteOrderID, this.recordLockSource);
    }

    private allowUnCancelOrder() {
        //console.log("setQuoteOrderLineLoadStatus", line.quoteOrderLineID, line);
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;
        dialogConfig.data = {
            header: 'UnCancel Order?',
            message: 'You are about to UnCancel this order and set it to Planned.  Confirm to continue.'
        }

        const dialogRef = this.dialog.open(ConfirmDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(dialogResult => {
            if (!dialogResult) return;

            this.utilService.isLoading = true;
            this.orderService.unCancelOrder(this.quoteOrder.quoteOrderID).subscribe(result => {
                this.utilService.isLoading = false;
                if (result.message != "OK") {
                    this.notificationService.showError("Error uncanceling order: " + result.message, "ERROR: UnCancel Order");
                }
                else {
                    this.notificationService.showSuccess("Successfully uncanceled order", "SUCCESS: UnCancel Order");
                    console.log("unCancelOrder", this.quoteOrder);
                }
                this.getQuoteOrder(this.quoteOrderID);
            });
        });
    }

    public getQuoteOrderLineSplit(evt: any) {
        //console.log("getQuoteOrderLineSplit:before", evt, this.quoteOrderLineSplit, this.quoteOrderLine);
        var quoteOrderLineID: number = evt.quoteOrderLineID ? evt.quoteOrderLineID : 0;
        var orderStopID: number = evt.orderStopID ? evt.orderStopID : 0;
        //this.getNewQuoteOrderLine(quoteOrderLineID);
        var dropID = this.dropStopEventTypes.find(t => t.code == "DLT")!.orderEventTypeID;
        var pickID = this.pickStopEventTypes.find(t => t.code == "HLT")!.orderEventTypeID;
        this.quoteOrderLineSplit = {
            newQuoteOrderLine: this.quoteOrderLine,
            quoteOrderID: this.quoteOrder.quoteOrderID,
            quoteOrderLineID: quoteOrderLineID,
            orderStopID: orderStopID,
            dropOrderEventTypeID: dropID,
            pickOrderEventTypeID: pickID,
            dropOrderEventCode: "DLT",
            pickOrderEventCode: "HLT"
        }
        this.getNewQuoteOrderLine(quoteOrderLineID);
        this.quoteOrderLineSplit.newQuoteOrderLine = this.quoteOrderLine;
        this.isSplit = true;
        //console.log("getQuoteOrderLineSplit:after", quoteOrderLineID, orderStopID, this.quoteOrderLineSplit, this.quoteOrderLine);
    }

    private refreshLoadSplit() {
        this.quoteOrderLineSplit = {} as QuoteOrderLineSplit;
    }

    public saveQuoteOrderLineSplit() {
        this.recordLockAction = "saveQuoteOrderLineSplit";
        this.recordLockService.checkHasRecordLock("QuoteOrder", this.quoteOrder.quoteOrderID, this.recordLockSource);
    }

    private allowSaveQuoteOrderLineSplit() {
        if (this.validateFields("split")) {
            this.utilService.isLoading = true;
            this.quoteOrderLine.destinationCityID = this.quoteOrderLine.originCityID;
            this.quoteOrderLineSplit.newQuoteOrderLine.quoteOrderLineID = 0;
            var employeeID = parseInt(localStorage.getItem('EmployeeId')!);
            this.quoteOrderLineSplit.newQuoteOrderLine.createdByEmployeeId = employeeID;
            this.utilService.setAuditNotes("Order Entry: Save Split Load");
            this.orderService.saveQuoteOrderLineSplit(this.quoteOrderLineSplit).subscribe(result => {
                this.utilService.isLoading = false;
                if (result.resultCollection.length > 0) {
                    this.isSplit = false;
                    this.isEditQuoteOrderLine = false;
                    this.quoteOrderLine = Object.assign({}, result.resultCollection[0]);
                    this.setMinMaxDates();
                    this.quoteOrderLineList.push(result.resultCollection[0]);
                    this.notificationService.showSuccess("Created Load Split " + this.quoteOrder.quoteOrderID + "-" + this.quoteOrderLine.legNumber + ".", "SUCCESS: Create Load Split");
                    this.getQuoteOrder(this.quoteOrderID);
                    console.log("saveQuoteOrderLineSplit", "quoteOrderLine", this.quoteOrderLine, result);
                }
                else {
                    this.notificationService.showError("Error saving Load", "ERROR: Save Load");
                }
            });
        }
    }

    public isValidUrl(url: string) {
        this.isValidExternalTrackingLink = this.utilService.isValidURL(url);
    }

    public saveQuoteOrderLine() {
        this.recordLockAction = "saveQuoteOrderLine";
        this.recordLockService.checkHasRecordLock("QuoteOrder", this.quoteOrder.quoteOrderID, this.recordLockSource);
    }

    private allowSaveQuoteOrderLine() {
        //console.log("saveQuoteOrderLine", this.quoteOrderLine.originCityID, this.quoteOrderLineShipper.cityId, this.quoteOrderLine.destinationCityID, this.quoteOrderLineConsignee.cityId);
        if (this.quoteOrderLineShipper.cityID > 0) {
            this.quoteOrderLine.originCityID = this.quoteOrderLineShipper.cityID;
        }
        if (this.quoteOrderLineConsignee.cityID > 0) {
            this.quoteOrderLine.destinationCityID = this.quoteOrderLineConsignee.cityID;
        }
        this.quoteOrderLine.originalPickupDate = this.utilService.getDateAsUTC(this.quoteOrderLineShipperEarliestDate);
        this.quoteOrderLine.originalPickupEndDate = this.utilService.getDateAsUTC(this.quoteOrderLineShipperLatestDate);
        this.quoteOrderLine.originalDeliveryDate = this.utilService.getDateAsUTC(this.quoteOrderLineConsigneeEarliestDate);
        this.quoteOrderLine.originalDeliveryEndDate = this.utilService.getDateAsUTC(this.quoteOrderLineConsigneeLatestDate);
        if (this.isSplit) {
            this.allowSaveQuoteOrderLineSplit();
        }
        else {
            this.utilService.isLoading = true;
            if (this.validateFields("quoteorderline")) {
                this.utilService.setAuditNotes("Order Entry: Save Load");
                this.orderService.saveQuoteOrderLine(this.quoteOrderLine).subscribe(result => {
                    this.utilService.isLoading = false;
                    if (result != null) {
                        this.quoteOrderLine = Object.assign({}, result);
                        this.setMinMaxDates();
                        this.notificationService.showSuccess("Saved Load " + this.quoteOrder.quoteOrderID + "-" + this.quoteOrderLine.legNumber + ".", "SUCCESS: Save Load");
                        this.getQuoteOrder(this.quoteOrderID);
                        console.log("saveOrder", "quoteOrderLine", this.quoteOrderLine);
                    }
                    else {
                        this.notificationService.showError("Error saving Load", "ERROR: Save Load");
                    }
                });
            }
        }
    }

    public canSaveQuoteOrderLine() {
        var isValid: boolean = true;
        if (this.quoteOrderLine.rateIndexID < 1) {
            isValid = false;
        }
        if (this.quoteOrderLine.modeID < 1) {
            isValid = false;
        }
        if (this.quoteOrderLine.equipmentClassID == undefined || this.quoteOrderLine.equipmentClassID < 1) {
            isValid = false;
        }
        if (this.quoteOrderLine.trailerTypeID < 1) {
            isValid = false;
        }
        if (!this.isValidExternalTrackingLink) {
            isValid = false;
        }
        return isValid;
    }

    public cancelSaveQuoteOrderLine() {
        this.isSplit = false;
        this.isEditQuoteOrderLine = false;
        this.refreshLoadSplit();
    }

    public getSplitOrderStop(quoteOrderLineID: number, stopID: number) {
        //this.getAddSplitOrderStops(quoteOrderLineID, stopID, "after");
        this.utilService.isLoading = true;
        this.getSearchQuoteOrderLine(quoteOrderLineID);
        this.orderService.getOrderStopsByLineId(quoteOrderLineID).subscribe(result => {
            this.utilService.isLoading = false;
            if (result.resultCollection.length > 0) {
                var stops: OrderStop[] = result.resultCollection;
                var stop: OrderStop = stops.find(t => t.orderStopId == stopID)!;
                //var nextStop = stops.find(t => t.stopSequence = stop.stopSequence + 1);
                var stopsAfter: OrderStop[] = stops.filter(t => t.stopSequence > stop.stopSequence);
                var nextStop: OrderStop = {} as OrderStop;
                if (stopsAfter.length > 0) {
                    nextStop = stopsAfter.reduce(function (prev, current) {
                        return (prev.stopSequence < current.stopSequence) ? prev : current
                    });
                }
                console.log("getSplitOrderStop:nextStop", nextStop);
                if (nextStop) {
                    this.minStopDate = new Date(stop.earliestDate);
                    this.maxStopDate = new Date(nextStop.earliestDate);
                    this.quoteOrderLineShipperEarliestDate = new Date(stop.earliestDate);
                    this.quoteOrderLineShipperLatestDate = new Date(stop.earliestDate);
                    this.quoteOrderLineConsigneeEarliestDate = new Date(stop.earliestDate);
                    this.quoteOrderLineConsigneeLatestDate = new Date(stop.earliestDate);
                    this.dateChanged("quoteOrderLineShipper", "earliest");
                }
                else {
                    this.notificationService.showError("Error loading next stop", "ERROR: Split Load");
                    this.isSplit = false;
                }
            }
            else {
                this.notificationService.showError("Error loading stops", "ERROR: Split Load");
                this.isSplit = false;
            }
        });
    }

    public updateTotalAccessorialCharge(evt: any) {
        this.quoteOrder.totalAccessorialCharge = evt;
    }

    public updatePayDetailTotal(evt: any) {
        this.payDetailTotal = evt;
        this.setupOrderTotals();
    }

    public updateThirdPartyPayTotal(evt: any) {
        this.thirdPartyPayTotal = evt;
        this.setupOrderTotals();
    }

    private getNewQuoteOrderLine(quoteOrderLineID: number) {
        var isFirst: boolean = (quoteOrderLineID == 0);
        this.isEditQuoteOrderLine = false;
        if (!isFirst) {
            this.isEditQuoteOrderLine = true;
            this.quoteOrderLine = {} as QuoteOrderLine;
            this.minStopDate = undefined;
            this.maxStopDate = undefined;
            //this.quoteOrderLine = Object.assign({}, this.quoteOrderLineList[this.quoteOrderLineList.length - 1]);
            //this.quoteOrderLine.quoteOrderLineID = 0;
        }
        if (this.quoteOrderID > 0) {
            this.quoteOrderLine.quoteOrderID = this.quoteOrder.quoteOrderID;
            //this.quoteOrderLine.orderNumber = this.quoteOrder.quoteOrderID.toString();
            //this.quoteOrderLine.orderNumber = this.quoteOrder.orderNumber.toString();
            if (isFirst) {
                this.quoteOrderLine.shipperID = this.quoteOrder.shipperId;
                this.quoteOrderLine.consigneeID = this.quoteOrder.consigneeId;
                this.quoteOrderLine.accessorialCost = this.quoteOrder.totalAccessorialCharge
                this.quoteOrderLine.price = this.quoteOrder.lineHaulCharge;
                //this.quoteOrderLine.pieces = this.quoteOrder.pieces;
                //this.quoteOrderLine.weight = this.quoteOrder.weight;
                this.quoteOrderLine.createdByEmployeeId = this.quoteOrder.createdByEmployeeID;
                this.quoteOrderLine.lastModifiedByEmployeeID = this.quoteOrder.lastModifiedByEmployeeID;
            }
            else {
                var employeeID = parseInt(localStorage.getItem('EmployeeId')!);
                this.quoteOrderLine.createdByEmployeeId = employeeID;
                this.quoteOrderLine.lastModifiedByEmployeeID = employeeID;
            }
        }
        //var line = Object.assign({}, this.quoteOrderLine);
        this.quoteOrderLine.quoteOrderLineID = 0;
        this.quoteOrderLine.legNumber = this.quoteOrderLineList.length + 1;
        if (isFirst) {
            if (this.pickCityID > 0) {
                this.quoteOrderLine.originCityID = this.pickCityID;
            }
            else {
                this.quoteOrderLine.originCityID = this.shipper.cityID;
            }
            if (this.dropCityID > 0) {
                this.quoteOrderLine.destinationCityID = this.dropCityID;
            }
            else {
                this.quoteOrderLine.destinationCityID = this.consignee.cityID;
            }
            this.quoteOrderLine.originalPickupDate = this.utilService.getDateAsUTC(this.shipperEarliestDate);
            this.quoteOrderLine.originalPickupEndDate = this.utilService.getDateAsUTC(this.shipperLatestDate);
            this.quoteOrderLine.originalDeliveryDate = this.utilService.getDateAsUTC(this.consigneeEarliestDate);
            this.quoteOrderLine.originalDeliveryEndDate = this.utilService.getDateAsUTC(this.consigneeLatestDate);
        }
        else {
            var idx: number = this.quoteOrderLineList.findIndex(t => t.quoteOrderLineID == quoteOrderLineID);
            ////this.quoteOrderLineShipper = {} as Customer;
            //this.quoteOrderLineShipperEarliestDate = new Date(this.quoteOrderLineList[idx].originalPickupDate);
            //this.quoteOrderLineShipperLatestDate = new Date(this.quoteOrderLineList[idx].originalPickupEndDate);
            //this.customerFormGroup.controls["quoteOrderLineShipperCtrl"].setValue("");
            //this.getCustomer(this.quoteOrderLineList[idx].consigneeID, "quoteOrderLineShipper");

            this.getSplitOrderStop(quoteOrderLineID, this.quoteOrderLineSplit.orderStopID);
            //this.minStopDate = new Date(this.quoteOrderLineList[idx].originalPickupDate);
            //this.maxStopDate = new Date(this.quoteOrderLineList[idx].originalDeliveryDate);

            //this.quoteOrderLineShipperEarliestDate = new Date(this.quoteOrderLineList[idx].originalPickupDate);
            //this.quoteOrderLineShipperLatestDate = new Date(this.quoteOrderLineList[idx].originalPickupEndDate);
            ////this.quoteOrderLineShipperNotesList.length = 0;

            ////this.quoteOrderLineConsignee = {} as Customer;
            ////this.getCustomer(this.quoteOrderLineList[idx].consigneeID, "quoteOrderLineConsignee");
            //this.quoteOrderLineConsigneeEarliestDate = new Date(this.quoteOrderLineList[idx].originalPickupDate);
            //this.quoteOrderLineConsigneeLatestDate = new Date(this.quoteOrderLineList[idx].originalPickupEndDate);

            this.quoteOrderLineShipper = {} as SearchCustomer;
            this.customerFormGroup.controls["quoteOrderLineShipperCtrl"].setValue("");
            //this.quoteOrderLineConsigneeNotesList.length = 0;

            this.quoteOrderLine.consigneeID = this.quoteOrderLineList[idx].consigneeID;
            this.quoteOrderLine.modeID = this.quoteOrderLineList[idx].modeID;
            this.quoteOrderLine.equipmentClassID = this.quoteOrderLineList[idx].equipmentClassID;
            this.quoteOrderLine.trailerTypeID = this.quoteOrderLineList[idx].trailerTypeID;
            this.quoteOrderLine.trailerLength = this.quoteOrderLineList[idx].trailerLength;
            this.quoteOrderLine.roundTrip = this.quoteOrderLineList[idx].roundTrip;
            this.quoteOrderLine.quoteOrderLinePriorityID = this.quoteOrderLineList[idx].quoteOrderLinePriorityID;
            this.quoteOrderLine.pieces = this.quoteOrderLineList[idx].pieces;
            this.quoteOrderLine.weight = this.quoteOrderLineList[idx].weight;
            this.quoteOrderLine.commodityTypeID = this.quoteOrderLineList[idx].commodityTypeID;
        }

        var orderType: QuoteOrderType = {} as QuoteOrderType;
        if (this.orderFormType == "Order" || this.orderFormType == "Quote" || this.orderFormType == "RFQ" || this.orderFormType == "Can Get") {
            orderType = this.quoteOrderTypeList.find(t => t.description == this.orderFormType || t.abbrev == this.orderFormType)!;
        }
        else {
            orderType = this.quoteOrderTypeList.find(t => t.description == "Order")!;
        }
        var invoiceStatus = this.invoiceStatusList.find(t => t.abbrev == "PND");
        this.quoteOrderLine.quoteOrderLineTypeID = orderType!.quoteOrderTypeID;
        this.quoteOrderLine.invoiceStatusId = invoiceStatus!.invoiceStatusId;

        this.dateChanged("quoteOrderLineShipper", "earliest");
        this.dateChanged("quoteOrderLineConsignee", "earliest");

        //this.quoteOrderLineList.push(this.quoteOrderLine);
        console.log("getNewQuoteOrderLine", quoteOrderLineID, orderType, this.quoteOrderLine);
    }

    private setQuoteOrderTypeFromFormType() {
        if (this.quoteOrderTypeList.length > 0) {
            var orderType: QuoteOrderType = {} as QuoteOrderType;
            if (this.orderFormType == "Order" || this.orderFormType == "Quote" || this.orderFormType == "RFQ" || this.orderFormType == "Can Get") {
                orderType = this.quoteOrderTypeList.find(t => t.description == this.orderFormType || t.abbrev == this.orderFormType)!;
            }
            else {
                orderType = this.quoteOrderTypeList.find(t => t.description == "Order")!;
            }
            this.quoteOrder.quoteOrderTypeID = orderType!.quoteOrderTypeID;
            //console.log("setQuoteOrderTypeFromFormType", orderType);
        }
    }

    public getNewQuoteOrder() {
        this.quoteOrderID = 0;
        this.externalOrderNumber = "";
        this.billToID = 0;
        this.billTo = {} as SearchCustomer;
        this.customerFormGroup.controls["billToCtrl"].setValue("");
        //this.billToNotesList.length = 0;

        this.shipperID = 0;
        this.shipper = {} as SearchCustomer;
        this.shipperEarliestDate = new Date();
        this.shipperLatestDate = this.utilService.addMinutes(this.shipperEarliestDate, 30);
        this.customerFormGroup.controls["shipperCtrl"].setValue("");
        //this.shipperNotesList.length = 0;

        this.pickCityID = 0;
        //this.customerFormGroup.controls["pickCityCtrl"].setValue("");

        this.consigneeID = 0;
        this.consignee = {} as SearchCustomer;
        this.consigneeEarliestDate = this.utilService.addMinutes(this.shipperLatestDate, 60);
        this.consigneeLatestDate = this.utilService.addMinutes(this.consigneeEarliestDate, 30);
        this.customerFormGroup.controls["consigneeCtrl"].setValue("");
        //this.consigneeNotesList.length = 0;

        this.dropCityID = 0;
        //this.customerFormGroup.controls["dropCityCtrl"].setValue("");

        this.opportunity = {} as Opportunity;

        this.isPrepared = false;
        this.isMasterOrderLocked = false;

        var invoiceStatusID = 0;
        if (this.invoiceStatusList.length > 0) {
            invoiceStatusID = this.invoiceStatusList.find(t => t.name == "Pending")!.invoiceStatusId;
        }

        var productID = 0;
        if (this.productList.length > 0) {
            productID = this.productList.find(t => t.productName == "Brokerage")!.productId;
            this.opportunity.productId = productID;
        }

        this.createdByEmployeeName = "";
        this.lastModifiedByEmployeeName = "";

        var employeeID = parseInt(localStorage.getItem('EmployeeId')!);

        this.isExpedite = false;

        this.totalMiles = 0;
        this.payDetailTotal = 0;
        this.thirdPartyPayTotal = 0;

        this.dateChanged("shipper", "earliest");
        this.dateChanged("consignee", "earliest");

        var order = {
            quoteOrderID: 0,
            quoteNumber: 0,
            orderNumber: 0,
            customerID: 0,
            opportunityID: 0,
            lineHaulCharge: 0,
            invoiceStatusID: invoiceStatusID,
            orderOwnerEmployeeID: employeeID,
            internalNotes: "",
            externalNotes: "",
            isMasterOrder: false,
            //dueDate: new Date(),
            //launchDate: new Date(),
            createdDate: new Date()
        } as QuoteOrder;
        this.quoteOrder = order;

        this.setQuoteOrderTypeFromFormType();

        this.quoteOrderLine = {} as QuoteOrderLine;
        this.quoteOrderLine.quoteOrderLineID = 0;
        this.quoteOrderLine.modeID = -1;
        this.quoteOrderLine.equipmentClassID = -1;
        this.quoteOrderLine.trailerTypeID = -1;
        this.quoteOrderLine.quoteOrderLinePriorityID = -1;
        this.quoteOrderLine.commodityTypeID = -1;
        this.quoteOrderLine.pieces = 0;
        this.quoteOrderLine.weight = 0;
        this.minStopDate = undefined;
        this.maxStopDate = undefined;
        this.quoteOrderLineList.length = 0;
        this.shipperHasAppointment = false;
        this.consigneeHasAppointment = false;

        var priorityID = 0;
        if (this.quoteOrderLinePriorityList.length > 0) {
            priorityID = this.quoteOrderLinePriorityList.find(t => t.abbrev == "Regular")!.quoteOrderLinePriorityID;
            this.quoteOrderLine.quoteOrderLinePriorityID = priorityID;
        }

        this.setDefaults();

        //this.getNewQuoteOrderLine(0);
        //this.quoteOrderLineList.push(this.quoteOrderLine);
        console.log("getNewQuoteOrder", this.quoteOrder, this.quoteOrderLine);
        //console.log("getNewQuoteOrder", this.shipperEarliestDate, this.shipperLatestDate, this.consigneeEarliestDate, this.consigneeLatestDate);
    }

    private initRecordLock() {
        this.recordLockAction = "";
        //if (this.recordLockService.continueRecordLockObserver.closed) {
        //}
        //if (this.recordLockService.timeoutRecordLockObserver.closed) {
        //}
        //if (this.recordLockService.timeoutConfirmObserver.closed) {
        //}
        //if (this.recordLockService.hasRecordLockObserver.closed) {
        //}
        if (this.recordLockService.continueRecordLockObserver.closed) {
        }
        this.recordLockService.continueRecordLockObserver.subscribe(result => {
            //console.log("order-form:continueRecordLockObserver", this.hasRecordLock, this.recordLockService.isContinueRecordLock, this.recordLockService.isLocked);
            this.hasRecordLock = !this.recordLockService.isLocked;
            if (this.recordLockService.isContinueRecordLock) {
                //this.continueRecordLockAction();
            }
        });
        this.recordLockService.cancelOverrideObserver.subscribe(result => {
            //console.log("order-form:cancelOverrideObserver", this.hasRecordLock, this.recordLockService.isCancelOverride, this.recordLockService.isLocked);
            if (this.recordLockService.isCancelOverride) {
                //this.closeTrackingInfo(this.selectedTrackingBoard);
                this.recordLockService.resetRecordLock();
                this.hasRecordLock = false;
                this.getQuoteOrder(this.quoteOrder.quoteOrderID);
            }
        });
        this.recordLockService.timeoutRecordLockObserver.subscribe(result => {
            if (this.recordLockService.isRecordLockTimeout) {
                //console.log("order-form:handleRecordLock:isRecordLockTimeout", this.recordLockAction, this.quoteOrder.quoteOrderID, this.recordLockService.searchRecordLock);
                this.recordLockService.clearRecordLock("QuoteOrder", this.quoteOrder.quoteOrderID, this.recordLockSource);
                //this.recordLockService.clearRecordLock(this.recordLockService.recordLock.recordLockID);
                //this.continueRecordLockAction();
            }
        });
        this.recordLockService.timeoutConfirmObserver.subscribe(result => {
            if (this.recordLockService.isConfirmTimeout) {
                //console.log("order-form:handleRecordLock:timeoutConfirmObserver", this.recordLockAction, this.quoteOrder.quoteOrderID, this.recordLockService.searchRecordLock);
                this.recordLockService.resetRecordLock();
                //this.navigateQuoteOrder(this.quoteOrderID, true);
                this.getQuoteOrder(this.quoteOrder.quoteOrderID);
            }
        });
        this.recordLockService.hasRecordLockObserver.subscribe(result => {
            //console.log("order-form:hasRecordLockObserver", this.hasRecordLock, this.recordLockService.hasRecordLock, this.recordLockService.isLocked, this.recordLockAction);
            this.hasRecordLock = this.recordLockService.hasRecordLock;
            if (this.recordLockService.hasRecordLock) {
                if (this.recordLockAction == "saveOffer") {
                    this.allowSaveOffer();
                }
                else if (this.recordLockAction == "saveOrder") {
                    this.allowSaveOrder();
                }
                else if (this.recordLockAction == "syncOrder") {
                    this.allowSyncOrder();
                }
                else if (this.recordLockAction == "setQuoteOrderType") {
                    this.allowSetQuoteOrderType(this.setQuoteOrderTypeValue);
                }
                else if (this.recordLockAction == "cancelOrder") {
                    this.allowCancelOrder();
                }
                else if (this.recordLockAction == "unCancelOrder") {
                    this.allowUnCancelOrder();
                }
                else if (this.recordLockAction == "saveQuoteOrderLineSplit") {
                    this.allowSaveQuoteOrderLineSplit();
                }
                else if (this.recordLockAction == "saveQuoteOrderLine") {
                    this.allowSaveQuoteOrderLine();
                }
                this.recordLockAction = "";
            }
        });
    }

    public handleRecordLock() {
        //console.log("order-form:handleRecordLock", id);
        if (this.hasRecordLock) {
            this.recordLockAction = "";
            this.recordLockService.clearRecordLock("QuoteOrder", this.quoteOrder.quoteOrderID, this.recordLockSource);
            this.recordLockService.resetRecordLock();
            this.hasRecordLock = false;
        }
        else {
            this.recordLockService.handleRecordLock("QuoteOrder", this.quoteOrder.quoteOrderID, "Order Entry");
        }
    }
}
